/* Anwendung: Zustand, Befehle, Werkzeugleiste, Tastatur, Dateien. Die Seitenleiste steckt in panels.js. */
import * as THREE from 'three';
import Bsp from './formats/bsp.ts';
import Cmd from './model/cmds.ts';
import Core from './core.ts';
import CtxMenu from './ctxmenu.js';
import { decompileBsp } from './services/decompile.ts';
import EntDefs from './model/entdefs.ts';
import Gens from './model/gens.ts';
import Groups from './model/groups.ts';
import I18n from './i18n.ts';
import Keys from './keys.ts';
import Leak from './formats/leak.ts';
import Light from './analysis/light.ts';
import Panels from './panels.js';
import Skybox from './formats/skybox.ts';
import Spr from './formats/spr.ts';
import { covers, displace, prepare, previewLines, sculpt, sculptableIds } from './model/sculpt.ts';
import TexAlign from './model/texalign.ts';
import Theme from './theme.js';
import { initBackendUi } from './ui/backend-ui.tsx';
import { createElement } from 'react';
import { closeAllDialogs, closeTop, openDialog } from './ui/modal.tsx';
import { BspExportDialog, ExportDialog } from './ui/dialogs/ExportDialog.tsx';
import { CheckDialog } from './ui/check/CheckPanel.tsx';
import { FindDialog, newFindState } from './ui/dialogs/FindDialog.tsx';
import { SnapshotDialog } from './ui/dialogs/SmallDialogs.tsx';
import { getDownloads, safeName } from './ui/dialogs/save.ts';
import View2D from './view2d.js';
import View3D from './view3d.js';
import Wad from './formats/wad.ts';
import { bundledNames, bundledText, isBundled } from './fgd/index.ts';
import { spritePathOf } from './model/entvisual.ts';
import { ModelStore } from '@xen-forge/mdl';
import { collectDropped } from './net/dropfiles.ts';
import { bspHasSky, skyToggles, VIEW_DEFAULTS } from './model/skyview.ts';
import { addFgdName, addToProfile, applyFgds, COMPILER_FGDS, effectiveFgds, fgdNames, FGD_KEY } from './model/modprofile.ts';
import { SAMPLE_TEXT } from './samples.js';
import { lookupSound, soundKey } from './net/sounds.ts';
import { createAssetHost } from './ui/assets/assets-ui.tsx';
const _ = I18n.t;
const app = (function () {
  'use strict';
  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));
  const SAVE_KEY = 'goldsrc-editor-autosave-v1';
  const GRIDS = [1, 2, 4, 8, 16, 32, 64, 128, 256];
  const AX = View2D.AX;

  const app = {
    history: new Core.History(Core.emptyDoc()),
    pv: null, sel: Cmd.emptySel(), selFace: null, faceSel: [], _sf: null, tool: 'select',
    grid: 16, snapOn: true, lock: true, spaceDown: false,
    depth: [[0, 128], [0, 128], [0, 128]], entDepth: [0, 0, 32],
    showAids: false, showEnts: VIEW_DEFAULTS.showEnts, showModels: VIEW_DEFAULTS.showModels, bsp: null, clipLine: null, clipKeep: 'front', views: [], activeView: null, v2: {}, v3: null,
    wads: new Wad.Registry(), wadNames: [], fgdTexts: new Map(), fgdPending: new Set(), fgdIsAuto: false, fgdVer: 0, fgdKey: null, fgdState: { loaded: [], missing: [], classes: 0, warnings: [] }, activeTex: 'DRKMTLT_WALL',
    shape: { kind: 'box', sides: 8 }, entClass: 'info_player_start', clipboard: null,
    layout: 'quad', single: 'top', fileName: 'neue_map', dirty: false, panels: null,
    get doc() { return this.history.doc; }
  };

  // ------------------------------------------------------------ Darstellung
  let colorCache = null;
  app.colors = function () {
    if (colorCache) return colorCache;
    const cs = getComputedStyle(document.documentElement), g = (n, d) => cs.getPropertyValue(n).trim() || d;
    colorCache = {
      bg: g('--vp-bg', '#0e1216'), grid: g('--vp-grid', '#1c242d'), gridMajor: g('--vp-grid-major', '#2d3947'), axis: g('--vp-axis', '#4a5b6e'),
      wire: g('--wire', '#8aa0b6'), sel: g('--accent', '#ffb000'), selFill: g('--sel-fill', 'rgba(255,176,0,0.16)'), dim: g('--fg-dim', '#8b97a3')
    };
    return colorCache;
  };
  app.themeChanged = function () {
    colorCache = null;
    if (app.v3) app.v3.setTheme();
    app.views.forEach((v) => v.invalidate());
  };

  // selFace = zuletzt gewählte Fläche; faceSel = alle gewählten Flächen. Wer selFace auf null setzt, leert beides.
  Object.defineProperty(app, 'selFace', {
    configurable: true, enumerable: true,
    get() { return this._sf; },
    set(v) { this._sf = v || null; this.faceSel = v ? [v] : []; }
  });
  app.setFaceSel = function (list) {
    const seen = new Set(), l = [];
    list.forEach((x) => { const k = x.b + ':' + x.f; if (!seen.has(k)) { seen.add(k); l.push({ b: x.b, f: x.f }); } });
    this.faceSel = l; this._sf = l.length ? l[l.length - 1] : null;
    const ns = Cmd.emptySel(); l.forEach((x) => ns.b.add(x.b));
    this.setSel(ns, true);
    this.status(_('ui.faces_selected', { count: l.length }));
  };
  app.faceKey = function () { return new Set(this.faceSel.map((x) => x.b + ':' + x.f)); };
  app.viewDoc = function () { return this.filtered(this.pv || this.history.doc); };
  // ------------------------------------------------------------ Gruppen und Sichtbarkeit
  app.vis = { manual: new Set(), auto: new Set(), groups: [] }; app.visVer = 0;
  const anyHidden = () => app.vis.manual.size || app.vis.auto.size || app.vis.groups.some((g) => g.hidden);
  app.filtered = function (doc) {
    if (!anyHidden()) return doc;
    const c = this._fc; if (c && c.doc === doc && c.ver === this.visVer) return c.out;
    const out = Groups.filterDoc(doc, Groups.hiddenIds(doc, this.vis)); this._fc = { doc: doc, ver: this.visVer, out: out }; return out;
  };
  app.hiddenSet = function () { return anyHidden() ? Groups.hiddenIds(this.doc, this.vis) : new Set(); };
  app.pointIdSet = function () { const s = new Set(); this.doc.ents.forEach((e, i) => { if (Groups.isPoint(e, i)) s.add(e.id); }); return s; };
  app.pruneVis = function () {
    const live = Groups.liveIds(this.doc), keep = (set) => { set.forEach((id) => { if (!live.has(id)) set.delete(id); }); };
    keep(this.vis.manual); this.vis.groups.forEach((g) => keep(g.members));
    this.vis.groups = this.vis.groups.filter((g) => g.members.size);
  };
  app.visChanged = function () {
    this.visVer++;
    const hid = this.hiddenSet(), ns = Cmd.emptySel();
    this.sel.b.forEach((id) => { if (!hid.has(id)) ns.b.add(id); }); this.sel.e.forEach((id) => { if (!hid.has(id)) ns.e.add(id); });
    this.sel = ns; if (this.selFace && !ns.b.has(this.selFace.b)) this.selFace = null;
    if (this.v3) { this.v3.setDoc(this.doc); this.v3.setSel(); }
    this.invalidateAll(); this.updateCounts(); this.panels.selChanged(); this.panels.visChanged(); this.scheduleSave();
  };
  app.scheduleSave = function () { this.dirty = true; clearTimeout(this.saveT); this.saveT = setTimeout(() => this.autosave(), 1200); this.ui.cloud.schedule(); };
  const selIdSet = () => Groups.selIds(app.sel);
  app.groupSel = function (name) {
    const ids = selIdSet(); if (!ids.size) { this.status(_('ui.select_first')); return; }
    this.vis.groups.forEach((g) => ids.forEach((id) => g.members.delete(id)));
    const g = { id: Core.newId(), name: name || _('ui.group_default', { n: this.vis.groups.length + 1 }), hidden: false, sticky: true, members: ids };
    this.vis.groups.push(g); this.pruneVis(); this.visChanged(); this.status(_('ui.grouped', { name: g.name, count: ids.size }));
  };
  app.ungroupSel = function () {
    const ids = selIdSet(); let n = 0;
    this.vis.groups = this.vis.groups.filter((g) => { let hit = false; ids.forEach((id) => { if (g.members.has(id)) hit = true; }); if (hit) n++; return !hit; });
    this.visChanged(); this.status(n ? _('ui.ungrouped', { count: n }) : _('ui.no_group_sel'));
  };
  app.groupById = function (id) { return this.vis.groups.find((g) => g.id === id); };
  app.selectGroup = function (id, add) {
    const g = this.groupById(id); if (!g) return;
    const hid = this.hiddenSet(), sp = Groups.splitIds(this.doc, g.members), ns = add ? Cmd.cloneSel(this.sel) : Cmd.emptySel();
    sp.b.forEach((i) => { if (!hid.has(i)) ns.b.add(i); }); sp.e.forEach((i) => { if (!hid.has(i)) ns.e.add(i); });
    this.sel = ns; this.selFace = null; if (this.v3) this.v3.setSel(); this.invalidateAll(); this.panels.selChanged(); this.updateCounts();
  };
  app.groupAdd = function (id) { const g = this.groupById(id), ids = selIdSet(); if (!g || !ids.size) return; this.vis.groups.forEach((o) => ids.forEach((i) => o.members.delete(i))); ids.forEach((i) => g.members.add(i)); this.pruneVis(); this.visChanged(); };
  app.groupRemove = function (id) { const g = this.groupById(id); if (!g) return; selIdSet().forEach((i) => g.members.delete(i)); this.pruneVis(); this.visChanged(); };
  app.groupDelete = function (id) { this.vis.groups = this.vis.groups.filter((g) => g.id !== id); this.visChanged(); };
  app.groupToggle = function (id) { const g = this.groupById(id); if (g) { g.hidden = !g.hidden; this.visChanged(); } };
  app.groupIsolate = function (id) {
    const g = this.groupById(id); if (!g) return;
    this.vis.manual = new Set(); this.vis.auto = new Set(); this.vis.groups.forEach((o) => { o.hidden = o !== g; }); this.visChanged();
  };
  app.autoToggle = function (key) { if (this.vis.auto.has(key)) this.vis.auto.delete(key); else this.vis.auto.add(key); this.visChanged(); };
  app.hideSel = function () {
    const ids = selIdSet(); if (!ids.size) { this.status(_('ui.select_first_hide')); return; }
    ids.forEach((id) => this.vis.manual.add(id)); this.visChanged(); this.status(_('ui.hidden_n', { count: ids.size }));
  };
  app.isolateSel = function () {
    const ids = selIdSet(); if (!ids.size) { this.status(_('ui.select_first')); return; }
    const live = Groups.liveIds(this.doc); this.vis.manual = new Set(); live.forEach((id) => { if (!ids.has(id)) this.vis.manual.add(id); });
    this.vis.groups.forEach((g) => { g.hidden = false; }); this.vis.auto = new Set(); this.visChanged(); this.status(_('ui.isolated'));
  };
  app.showAll = function () {
    this.vis.manual = new Set(); this.vis.auto = new Set(); this.vis.groups.forEach((g) => { g.hidden = false; }); this.visChanged(); this.status(_('ui.all_shown'));
  };
  app.visText = function () { return Groups.toComment(this.doc, this.vis); };
  app.visRestore = function (text) {
    const d = Groups.fromText(text || ''); this.vis = d ? Groups.restore(this.doc, d, Core.newId) : { manual: new Set(), auto: new Set(), groups: [] };
    this.visVer++; if (this.v3) this.v3.setDoc(this.doc); this.invalidateAll(); this.updateCounts(); this.panels.visChanged();
  };
  app.selBBox = function () { return Cmd.selBBox(this.viewDoc(), this.sel); };
  app.status = function (msg) { $('#st-msg').textContent = msg; };
  app.setCursor = function (key, w) {
    const l = AX[key];
    $('#st-pos').textContent = l.hl + ' ' + Math.round(w[0]) + '   ' + l.vl + ' ' + Math.round(w[1]);
  };
  app.invalidateAll = function () { app.views.forEach((v) => v.invalidate()); };

  // ------------------------------------------------------------ Auswahl und Dokument
  app.setSel = function (ns, keepFace) {
    if (!keepFace && this.vis.groups.some((g) => g.sticky)) {
      const pe = this.pointIdSet(), hid = this.hiddenSet();
      ns = Groups.expand(this.vis.groups, this.sel, ns, (id) => pe.has(id));
      hid.forEach((id) => { ns.b.delete(id); ns.e.delete(id); });
    }
    this.sel = ns; this.vertSel = null;
    if (!keepFace) this.selFace = null;
    if (this.v3) this.v3.setSel();
    this.invalidateAll();
    this.panels.selChanged();
    this.updateCounts();
  };
  app.pruneSel = function () {
    this.pruneVis();
    const ids = new Set(), eids = new Set();
    this.doc.ents.forEach((e, i) => { e.brushes.forEach((b) => ids.add(b.id)); if (Cmd.isPointEnt(e, i)) eids.add(e.id); });
    const ns = Cmd.emptySel();
    this.sel.b.forEach((id) => { if (ids.has(id)) ns.b.add(id); });
    this.sel.e.forEach((id) => { if (eids.has(id)) ns.e.add(id); });
    this.faceSel = this.faceSel.filter((x) => ns.b.has(x.b)); this._sf = this.faceSel.length ? (this._sf && ns.b.has(this._sf.b) ? this._sf : this.faceSel[this.faceSel.length - 1]) : null;
    this.sel = ns;
  };
  app.preview = function (doc) {
    this.pv = doc;
    this.invalidateAll();
    if (this.v3 && this.v3.ok && this.v3.stats && this.v3.stats.brushes < 500) this.v3.setDoc(doc);
    this.panels.selChanged(true);
  };
  app.endPreview = function () {
    if (!this.pv) return;
    this.pv = null;
    if (this.v3) this.v3.setDoc(this.doc);
    this.invalidateAll();
    this.panels.selChanged();
  };
  app.commit = function (doc, label, sel) {
    this.pv = null;
    this.marks = null;   // Markierungen gehören zum geprüften Stand
    if (sel) { this.sel = sel; this.selFace = null; }
    this.history.push(doc, label);
    this.changed();
  };
  app.changed = function () {
    if (this.syncFgd()) this.autoLoadAssets();   // anderes Mod-Profil: andere Modelle werden gebraucht
    this.pruneSel();
    if (this.v3) { this.v3.setDoc(this.doc); this.v3.setSel(); }
    this.invalidateAll();
    this.updateButtons();
    this.updateCounts();
    this.panels.docChanged();
    if (this.light.result && !this.light.stale) { this.light.stale = true; this.updateLightUi(); }
    this.dirty = true;
    clearTimeout(this.saveT);
    this.saveT = setTimeout(() => (window.requestIdleCallback ? requestIdleCallback(() => this.autosave(), { timeout: 3000 }) : this.autosave()), 1200); // im Leerlauf, damit große Maps beim Arbeiten nicht ruckeln
    this.ui.cloud.schedule();
    this.scheduleCheckFlag();
  };
  app.updateCounts = function () {
    const d = this.viewDoc();
    let nb = 0, ne = 0;
    d.ents.forEach((e, i) => { nb += e.brushes.length; if (i > 0) ne++; });
    const s = this.sel;
    $('#st-cnt').textContent = _('ui.counts', { brushes: nb, ents: ne }) + (s.b.size || s.e.size ? ' · ' + _('ui.counts_sel', { b: s.b.size, e: s.e.size }) : '');
  };
  app.updateButtons = function () {
    $('#b-undo').disabled = !this.history.canUndo();
    $('#b-redo').disabled = !this.history.canRedo();
  };
  app.undo = function () {
    if (!this.history.canUndo()) return;
    const label = this.history.stack[this.history.i].label;
    this.history.undo(); this.changed(); this.status(_('ui.undone', { label: label }));
  };
  app.redo = function () {
    if (!this.history.canRedo()) return;
    this.history.redo(); this.changed();
    this.status(_('ui.redone', { label: this.history.stack[this.history.i].label }));
  };
  app.autosave = function () {
    if (this.bsp) { $('#st-save').textContent = _('ui.autosave_bsp'); return; }
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify({ name: this.fileName, text: Core.toText(this.doc) + this.visText(), tex: this.activeTex }));
      $('#st-save').textContent = _('ui.autosaved');
    } catch (err) { $('#st-save').textContent = _('ui.autosave_fail'); }
  };

  // ------------------------------------------------------------ Werkzeuge
  const TOOLS = [
    { k: 'select', n: 'ui.tool.select', key: 'V', svg: '<path d="M5 3l14 8-6 2 4 7-3 1-4-7-5 4z"/>' },
    { k: 'box', n: 'ui.tool.box', key: 'B', svg: '<path d="M4 8l8-4 8 4v8l-8 4-8-4z"/><path d="M4 8l8 4 8-4M12 12v8"/>' },
    { k: 'entity', n: 'ui.tool.entity', key: 'E', svg: '<circle cx="12" cy="10" r="4"/><path d="M12 14v7M8 21h8"/>' },
    { k: 'clip', n: 'ui.tool.clip', key: 'X', svg: '<path d="M3 20L21 4"/><rect x="5" y="5" width="8" height="8" stroke-dasharray="2 2"/>' },
    { k: 'texture', n: 'ui.tool.texture', key: 'M', svg: '<path d="M5 4h12v5H5z"/><path d="M17 6.5h3v5h-8v3"/><path d="M10 14.5h4V20h-4z"/>' },
    { k: 'vertex', n: 'ui.tool.vertex', key: 'C', svg: '<path d="M5 18L9 6l10 4-3 9z"/><rect x="3.5" y="16.5" width="3" height="3" fill="currentColor"/><rect x="7.5" y="4.5" width="3" height="3" fill="currentColor"/><rect x="17.5" y="8.5" width="3" height="3" fill="currentColor"/>' },
    { k: 'sculpt', n: 'ui.tool.sculpt', key: 'U', svg: '<path d="M3 18c3 0 4-9 9-9s6 9 9 9"/><path d="M3 21h18"/>' }
  ];
  app.setTool = function (t) {
    if (this.bsp && (t === 'box' || t === 'clip' || t === 'vertex' || t === 'texture' || t === 'sculpt')) { this.status(_('ui.bsp_readonly')); return; }
    if (this.tool !== t) { this.clipLine = null; this.vertSel = null; }
    this.tool = t;
    if (t === 'sculpt' && this.v3 && this.v3.fly) this.toggleFly(); // Flugmodus belegt die linke Maustaste zum Umsehen
    if (this.v3) this.v3.setSculptMode(t === 'sculpt');
    $$('.tool').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.tool === t)));
    this.renderOpts();
    this.invalidateAll();
    const c = t === 'select' || t === 'vertex' || (t === 'texture' && this.texMode === 'edit') ? 'default' : 'crosshair';
    if (t === 'texture') this.panels.showTab('tex');
    this.updatePaintCursor();
    this.views.forEach((v) => { if (v.canvas && v.key !== 'persp') v.canvas.style.cursor = c; });
    this.status(_('ui.toolhint.' + t));
  };

  // ------------------------------------------------------------ Textur-Modus: Bearbeiten und Anmalen
  app.texMode = 'edit';
  app.texPaint = { scope: 'face', align: false, src: null }; // src: aufgenommene Fläche { attrs, n } für „mit Skala und Ausrichtung“
  app.updatePaintCursor = function () { $('#views').classList.toggle('paint', this.tool === 'texture' && this.texMode === 'paint'); };
  app.setTexMode = function (m) {
    this.texMode = m; this.updatePaintCursor();
    this.renderOpts(); this.panels.texChanged(); if (this.tool === 'texture') this.panels.render();
    this.views.forEach((v) => { if (v.canvas && v.key !== 'persp') v.canvas.style.cursor = m === 'paint' ? 'crosshair' : 'default'; });
    this.status(_(m === 'paint' ? 'ui.paint_hint' : 'ui.toolhint.texture'));
  };
  /** Nimmt Textur (und bei einer einzelnen Fläche auch Ausrichtung) auf. fi = null: häufigste Textur des Brushes. */
  /** Ziel für Auswählen und Anmalen: Fläche, Brush oder Gruppe. Beim Wechsel weg von „Fläche“ gilt wieder der ganze Brush. */
  app.setTexScope = function (s) {
    this.texPaint.scope = s;
    if (s !== 'face' && this.faceSel.length) { this.selFace = null; if (this.v3) this.v3.setSel(); this.invalidateAll(); }
    this.renderOpts(); this.panels.selChanged();
  };
  app.pickTexture = function (id, fi) {
    const fb = Core.findBrush(this.doc, id); if (!fb) return;
    if (fi === null || fi === undefined || !fb.b.faces[fi]) { this.texPaint.src = null; this.setActiveTex(texOfBrush(fb.b), false); }
    else { const a = TexAlign.pick(fb.b, fi); this.texPaint.src = { attrs: a, n: Core.faceNormal(fb.b.verts, fb.b.faces[fi].vi) }; this.setActiveTex(a.tex, false); }
    this.status(_('ui.tex_picked', { tex: this.activeTex }));
    this.panels.texChanged(); this.renderOpts();
  };
  /** Malt mit der aktiven Textur an: eine Fläche, den Brush oder die ganze Gruppe des Brushes. */
  app.paintFace = function (id, fi) {
    const T = this.texPaint, fb = Core.findBrush(this.doc, id); if (!fb) return;
    const g = T.scope === 'group' ? this.vis.groups.find((x) => x.members.has(id)) : null;
    const { ids, only } = TexAlign.paintTarget(T.scope, id, fi, g ? Groups.splitIds(this.doc, g.members).b : null);
    const src = T.src, attrs = Object.assign({}, src ? src.attrs : {}, { tex: this.activeTex });
    const useAlign = !!(T.align && src);
    const d = TexAlign.apply(this.doc, ids, only, (b, i) => TexAlign.paintPatch(attrs, b, i, src ? src.n : null, useAlign));
    if (d === this.doc) { this.status(_('ui.paint_nothing')); return; }
    this.commit(d, _('cmd.paint'));
    this.status(_('ui.painted', { tex: this.activeTex }));
  };
  app.defaultTex = function () { return this.activeTex || 'NULL'; };
  app.setActiveTex = function (name, apply) {
    this.activeTex = name;
    if (apply) this.applyTex(name);
    this.panels.texChanged();
  };

  app.createShape = function (mn, mx) {
    const b = Gens.shapeBrush(this.shape.kind, mn, mx, { sides: this.shape.sides, tex: this.defaultTex() });
    if (!b) { this.status(_('ui.shape_invalid')); return; }
    const r = Cmd.addBrushList(this.doc, [b]);
    this.commit(r.doc, _('cmd.create_brush'), r.sel);
    const dAx = this.activeView && this.activeView.ax ? this.activeView.ax.d : 2;
    [0, 1, 2].forEach((i) => { if (i !== dAx) this.depth[i] = [mn[i], mx[i]]; });
    this.setTool('select'); // direkt weiter: Größe und Höhe lassen sich mit den Griffen in allen Ansichten nachziehen
  };
  app.placeEntity = function (o, cls) {
    if (cls) { this.entClass = cls; if (this.tool === 'entity') this.renderOpts(); }
    const r = Cmd.createEntity(this.doc, this.entClass, o);
    this.commit(r.doc, _('cmd.place_entity'), r.sel);
    this.status(_('ui.entity_at', { cls: this.entClass, pos: Cmd.fmtOrigin(o) }));
  };

  // Clip
  app.onClipChange = function () { this.invalidateAll(); this.renderOpts(); };
  app.applyClip = function () {
    const cl = this.clipLine;
    if (!cl) { this.status(_('ui.clip_line_first')); return; }
    if (!this.sel.b.size) { this.status(_('ui.no_brushes_sel')); return; }
    const ax = AX[cl.view], dx = cl.b[0] - cl.a[0], dy = cl.b[1] - cl.a[1], L = Math.hypot(dx, dy);
    if (L < 1) return;
    const n = [0, 0, 0]; n[ax.h] = -dy / L; n[ax.v] = dx / L;
    const p = [0, 0, 0]; p[ax.h] = cl.a[0]; p[ax.v] = cl.a[1];
    const r = Cmd.clip(this.doc, this.sel, n, Core.dot(n, p), this.clipKeep, this.defaultTex());
    this.clipLine = null;
    this.commit(r.doc, _('cmd.clip'), r.sel);
    this.renderOpts();
    this.status(_('ui.clipped', { count: r.sel.b.size }));
  };
  app.cancelClip = function () { this.clipLine = null; this.onClipChange(); };

  // ------------------------------------------------------------ Bearbeiten
  const hasSel = () => app.sel.b.size || app.sel.e.size;
  app.center = function () {
    const bb = this.selBBox();
    return bb ? [0, 1, 2].map((i) => Math.round((bb.min[i] + bb.max[i]) / 2)) : [0, 0, 0];
  };
  app.del = function () {
    if (!hasSel()) return;
    const r = Cmd.remove(this.doc, this.sel);
    this.commit(r.doc, _('cmd.delete'), r.sel);
  };
  app.carve = function () {
    if (!this.sel.b.size) { this.status(_('ui.select_first')); return; }
    const r = Cmd.carve(this.doc, this.sel);
    if (!r) { this.status(_('ui.carve_none')); return; }
    this.commit(r.doc, _('cmd.carve'), r.sel);
    this.status(_('ui.carved', { count: r.cut, pieces: r.pieces }));
  };
  app.merge = function () {
    const r = Cmd.merge(this.doc, this.sel);
    if (r.err) { this.status(r.err === 'count' ? _('ui.merge_count') : r.err === 'entity' ? _('ui.merge_entity') : _('ui.merge_convex')); return; }
    this.commit(r.doc, _('cmd.merge'), r.sel);
    this.status(_('ui.merged', { count: r.merged }));
  };
  app.dup = function () {
    if (!hasSel()) return;
    const g = Math.max(this.grid, 16), v = this.activeView && this.activeView.ax ? this.activeView.ax : AX.top, o = [0, 0, 0];
    o[v.h] = g; o[v.v] = -g;
    const r = Cmd.duplicate(this.doc, this.sel, o);
    this.commit(r.doc, _('cmd.duplicate'), r.sel);
  };
  app.copy = function () {
    if (!hasSel()) return;
    this.clipboard = Cmd.toClip(this.doc, this.sel);
    this.status(_('ui.copied', { b: this.sel.b.size, e: this.sel.e.size }));
  };
  app.paste = function () {
    if (!this.clipboard) return;
    this.pasteN = (this.pasteN || 0) + 1;
    const g = Math.max(this.grid, 16) * this.pasteN;
    const r = Cmd.paste(this.doc, this.clipboard, [g, -g, 0]);
    this.commit(r.doc, _('cmd.paste'), r.sel);
  };
  app.selectAll = function () {
    const ns = Cmd.emptySel();
    this.viewDoc().ents.forEach((e, i) => { e.brushes.forEach((b) => ns.b.add(b.id)); if (Cmd.isPointEnt(e, i)) ns.e.add(e.id); });
    this.setSel(ns);
  };
  app.nudge = function (d) {
    if (!hasSel()) return;
    this.commit(Cmd.move(this.doc, this.sel, d, this.lock), _('cmd.move'));
  };
  app.rotate90 = function (axis, dir) {
    if (!hasSel()) return;
    this.commit(Cmd.rotate(this.doc, this.sel, axis, 90 * dir, this.center(), this.lock), _('cmd.rotate'));
  };
  app.mirror = function (axis) {
    if (!hasSel()) return;
    this.commit(Cmd.mirror(this.doc, this.sel, axis, this.center(), this.lock), _('cmd.mirror'));
  };
  app.applyTex = function (name) {
    if (this.faceSel.length > 1) {
      this.commit(Cmd.setFaces(this.doc, this.faceSel, { tex: name }), _('cmd.texture_faces', { count: this.faceSel.length }));
      return true;
    }
    if (this.selFace && this.sel.b.has(this.selFace.b)) {
      this.commit(Cmd.setFace(this.doc, this.selFace.b, this.selFace.f, { tex: name }), _('cmd.texture_face'));
      return true;
    }
    if (this.sel.b.size) { this.commit(Cmd.applyTexture(this.doc, this.sel, name), _('cmd.texture')); return true; }
    return false;
  };
  app.toEntity = function (cls) { if (this.sel.b.size) this.commit(Cmd.brushesToEntity(this.doc, this.sel, cls), _('cmd.to_entity', { cls: cls })); };
  app.toWorld = function () { if (this.sel.b.size) this.commit(Cmd.brushesToWorld(this.doc, this.sel), _('cmd.to_world')); };

  // ------------------------------------------------------------ Ansicht
  app.allBox = function () {
    if (this.bsp) {
      const sc = this.bsp.scene, bb = { min: sc.min.slice(), max: sc.max.slice() };
      this.doc.ents.forEach((e, i) => { if (Cmd.isPointEnt(e, i)) { const o = Core.parseVec(e.kv.origin); if (o) for (let k = 0; k < 3; k++) { bb.min[k] = Math.min(bb.min[k], o[k]); bb.max[k] = Math.max(bb.max[k], o[k]); } } });
      return bb;
    }
    const bs = Core.allBrushes(this.doc).map((x) => x.b), pts = [];
    this.doc.ents.forEach((e, i) => { if (Cmd.isPointEnt(e, i)) { const o = Core.parseVec(e.kv.origin); if (o) pts.push(o); } });
    if (!bs.length && !pts.length) return { min: [-512, -512, -64], max: [512, 512, 448] };
    const bb = bs.length ? Core.bboxOf(bs) : { min: pts[0].slice(), max: pts[0].slice() };
    pts.forEach((o) => { for (let i = 0; i < 3; i++) { bb.min[i] = Math.min(bb.min[i], o[i]); bb.max[i] = Math.max(bb.max[i], o[i]); } });
    return bb;
  };
  app.fit = function (all) {
    const box = !all && hasSel() ? this.selBBox() : this.allBox();
    this.resizeAll();
    // Auswahl mit reichlich Rand (man soll die Umgebung noch sehen), ganze Karte knapper
    const sub = !all && hasSel();
    Object.keys(this.v2).forEach((k) => this.v2[k].fit(box, sub ? 2.2 : 1.25));
    if (this.v3) this.v3.fit(box, sub ? 1.5 : 0.9);
  };
  app.resizeAll = function () { this.views.forEach((v) => v.resize()); };
  app.setLayout = function (mode, key) {
    if (window.innerWidth <= 900) mode = 'single';
    this.layout = mode;
    if (key) this.single = key;
    const keyShown = mode === 'single' ? this.single : null;
    $('#views').classList.toggle('single', mode === 'single');
    $$('.pane').forEach((p) => p.classList.toggle('on', p.dataset.view === keyShown));
    $$('#vtabs [data-v]').forEach((b) => b.classList.toggle('on', mode === 'single' && b.dataset.v === this.single));
    $('#b-quad').classList.toggle('on', mode === 'quad');
    if (keyShown) this.activeView = this.views.find((v) => v.key === keyShown) || this.activeView;
    $('#b-fly').disabled = !(this.v3 && this.v3.ok) || (mode === 'single' && this.single !== 'persp');
    requestAnimationFrame(() => { this.resizeAll(); });
  };
  app.toggleFly = function () {
    if (!this.v3 || !this.v3.ok) return;
    const on = !this.v3.fly;
    if (on && this.tool === 'sculpt') this.setTool('select');
    this.v3.setFly(on);
    $('#b-fly').classList.toggle('on', on);
    if (on) { this.activeView = this.v3; this.status(_('ui.fly_hint')); }
  };

  // ------------------------------------------------------------ Lichtvorschau
  app.light = { on: false, res: 16, shadows: true, bounce: true, flip: false, exposure: 1, result: null, job: null, stale: false };
  // Anzeige-Schalter beim Öffnen einer Karte auf den Anfangszustand setzen (Wert und Kästchen zusammen)
  app.resetViewToggles = function () {
    this.showEnts = VIEW_DEFAULTS.showEnts; $('#c-ents').checked = VIEW_DEFAULTS.showEnts;
    this.showModels = VIEW_DEFAULTS.showModels; $('#c-models').checked = VIEW_DEFAULTS.showModels;
    if (this.v3) { this.v3.hideSky = VIEW_DEFAULTS.hideSky; this.v3.hideCeil = VIEW_DEFAULTS.hideCeilings; }
    $('#c-hidesky').checked = VIEW_DEFAULTS.hideSky; $('#c-hideceil').checked = VIEW_DEFAULTS.hideCeilings;
  };
  // Schalter für den Himmel in der BSP-Ansicht (Sky-Flächen ausblenden, Skybox an/aus)
  app.updateSkyUi = function () {
    if (this.bsp && this.bsp.hasSky === undefined) this.bsp.hasSky = bspHasSky(this.bsp.scene.polys);
    const sh = skyToggles(this.bsp, !!this.skybox.mats), ok = this.v3 && this.v3.ok;
    $('#l-hidesky').hidden = !ok || !sh.hideSky;
    $('#l-skyon').hidden = !ok || !sh.skybox;
    $('#l-models').hidden = !ok || !this.models.keys().length;   // der Schalter erscheint, sobald ein Modell geladen ist
    $('#l-hideceil').hidden = !ok || !sh.hideCeilings;
    $('#c-skyon').checked = !!this.skybox.on;
  };
  app.updateLightUi = function () {
    this.updateSkyUi();
    const L = this.light, ok = this.v3 && this.v3.ok;
    $('#lightctl').hidden = !ok;
    if (!ok) return;
    $('#b-light').setAttribute('aria-pressed', String(L.on));
    $('#b-lrebake').hidden = !!this.bsp || !L.result || !!L.job;
    if (this.bsp) { $('#b-light').setAttribute('aria-pressed', String(L.on)); $('#l-info').textContent = this.bsp.scene.atlas ? _('ui.light_bsp') : _('ui.light_bsp_none'); return; }
    let t = '';
    if (L.job) t = _('ui.light_progress', { pct: Math.round(L.job.progress() * 100) });
    else if (L.result) t = _('ui.light_result', { lights: L.result.lights, luxels: L.result.luxels, secs: (L.result.ms / 1000).toFixed(1) }) + (L.result.dark ? ' · ' + _('ui.light_dark', { count: L.result.dark }) : '') + (L.stale ? ' · ' + _('ui.light_stale') : '');
    $('#l-info').textContent = t;
  };
  app.cancelLight = function () { this.light.job = null; this.updateLightUi(); };
  app.bakeLight = function () {
    const L = this.light;
    if (this.bsp) { this.status(_('ui.light_bsp_baked')); return; }
    if (!this.v3 || !this.v3.ok) return;
    const doc = this.doc;
    if (!Light.collect(doc).length) { this.status(_('ui.light_none')); return; }
    let job;
    try { job = Light.createJob(doc, { res: L.res, shadows: L.shadows, ambient: 0, bounce: L.bounce, flipSun: L.flip }); } catch (err) { this.status(err.message); return; }
    L.job = job;
    const tick = () => {
      if (L.job !== job) return;
      let done = false;
      try { done = job.step(14); } catch (err) { L.job = null; this.status(_('ui.light_failed', { msg: err.message })); this.updateLightUi(); return; }
      if (!done) { this.updateLightUi(); requestAnimationFrame(tick); return; }
      L.job = null; L.result = job.result; L.stale = false; L.on = true;
      this.v3.setLight(L.result, true, L.exposure);
      this.updateLightUi();
      this.status(_(job.result.bounce ? 'ui.light_done_bounce' : 'ui.light_done', { luxels: job.result.luxels, res: Math.round(job.result.res * 10) / 10 }));
    };
    this.status(_('ui.light_computing'));
    this.updateLightUi();
    requestAnimationFrame(tick);
  };
  app.toggleLight = function () {
    const L = this.light;
    if (this.bsp) { L.on = !L.on; this.v3.setBspLit(L.on); this.updateLightUi(); return; }
    if (L.job) { this.cancelLight(); return; }
    if (L.on) { L.on = false; this.v3.setLight(L.result, false); this.updateLightUi(); return; }
    if (L.result && !L.stale) { L.on = true; this.v3.setLight(L.result, true, L.exposure); this.updateLightUi(); return; }
    if (L.result && L.stale) { L.on = true; this.v3.setLight(L.result, true, L.exposure); this.updateLightUi(); this.status(_('ui.light_old')); return; }
    this.bakeLight();
  };

  app.snapshot = async function () {
    if (!this.v3 || !this.v3.ok) { this.status(_('ui.no_3d')); return; }
    let url;
    try { url = this.v3.snapshot(); } catch (err) { url = null; }
    if (!url || url.length < 200) { this.status(_('ui.snap_failed')); return; }
    const bin = atob(url.split(',')[1]), bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const dl = await getDownloads();
    const name = safeName(this.fileName) + '_3d.png';
    if (dl) {
      try { await dl.save({ filename: name, data: new Blob([bytes], { type: 'image/png' }) }); this.status(_('ui.snap_saved', { name: name })); return; } catch (err) {
        if (err && err.code === 'declined') { this.status(_('ui.save_cancelled')); return; }
      }
    }
    // Ersatz: Bild im Dialog zeigen, dort per Rechtsklick oder langem Tippen speichern
    openDialog((close) => createElement(SnapshotDialog, { url: url, onClose: () => close() }));
  };

  // ------------------------------------------------------------ Optionsleiste
  const h = (tag, props, ...kids) => {
    const e = document.createElement(tag);
    Object.keys(props || {}).forEach((k) => {
      if (k === 'on') Object.keys(props.on).forEach((ev) => e.addEventListener(ev, props.on[ev]));
      else if (k === 'class') e.className = props[k];
      else if (k === 'text') e.textContent = props[k];
      else if (k in e && k !== 'list') e[k] = props[k];
      else e.setAttribute(k, props[k]);
    });
    kids.flat().forEach((c) => { if (c !== null && c !== undefined) e.append(c.nodeType ? c : document.createTextNode(String(c))); });
    return e;
  };
  app.h = h;
  const numInput = (val, onChange, extra) => h('input', Object.assign({ type: 'number', class: 'inp num', value: String(val), step: 'any', on: { change: (e) => { const v = parseFloat(e.target.value); if (!isNaN(v)) onChange(v); } } }, extra || {}));

  // Werkzeug „Formen“: Klick oder Ziehen in 3D hebt an, Umschalt drückt ein (siehe model/sculpt.ts)
  app.sculptOpts = { radius: 192, strength: 16, mode: 'push', onlySel: false };
  /** Ids der formbaren Brushes, je Dokument gemerkt */
  app.sculptIds = function () { const c = this._sc; if (c && c.doc === this.doc) return c.ids; const ids = sculptableIds(this.doc); this._sc = { doc: this.doc, ids: ids }; return ids; };
  /** Brushes, auf die der Pinsel wirken darf: sichtbare, bei „Nur Auswahl“ nur gewählte; null = alle */
  app.sculptScope = function () {
    let only = this.sculptOpts.onlySel && this.sel.b.size ? this.sel : null;
    const hid = this.hiddenSet();
    if (hid.size) {
      const vis = Cmd.emptySel();
      this.doc.ents.forEach((e) => { if (!hid.has(e.id)) e.brushes.forEach((b) => { if (!hid.has(b.id) && (!only || only.b.has(b.id))) vis.b.add(b.id); }); });
      only = vis.b.size ? vis : { b: new Set(['']), e: new Set() };   // alles ausgeblendet: leere Menge, nicht „alle“
    }
    return only;
  };
  app.sculptStep = function (pos, invert, dir, from) {
    const sc = this.sculptOpts;
    return { cx: pos[0], cy: pos[1], cz: pos[2], dir: dir || 0, from: from, radius: sc.radius, mode: sc.mode, strength: sc.mode === 'push' ? (invert ? -sc.strength : sc.strength) : Math.min(1, sc.strength) };
  };
  /** Vorbereitung (Eckpunktgruppen) für Wirkung und Vorschau gemeinsam; je Dokument, Richtung, Auswahl und Sichtbarkeit gemerkt */
  app.sculptPrep = function (dir, st) {
    const d = dir || 0, only = this.sculptOpts.onlySel;
    let c = this._sp;
    if (!c || c.doc !== this.doc || c.dir !== d || c.only !== only || c.sel !== this.sel || c.vv !== this.visVer || !covers(c.prep, st)) {
      // nur Brushes in Pinselnähe (dreifacher Radius): bleibt der Pinsel darin, gilt die Vorbereitung für viele Mausbewegungen und Abdrücke
      c = this._sp = { doc: this.doc, dir: d, only: only, sel: this.sel, vv: this.visVer, prep: prepare(this.doc, this.sculptScope(), d, { cx: st.cx, cy: st.cy, cz: st.cz, r: st.radius * 3 }) };
    }
    return c.prep;
  };
  app.sculptAt = function (pos, invert, first, dir, from) {
    const st = this.sculptStep(pos, invert, dir, from);
    const r = sculpt(this.doc, this.sculptScope(), st, this.sculptPrep(dir, st));
    if (!r.moved) { if (first) this.status(_(r.rejected ? 'pn.sculpt_rejected' : 'pn.sculpt_nothing', { count: r.rejected })); return false; }
    // Auswahl und Gruppen bleiben: die neuen Brushes behalten ihre Ids
    if (first) { this.commit(r.doc, _('pn.sculpt_commit')); this.sculptOpen = true; return true; }
    // während des Strichs nur Ansichten und Verlauf nachziehen; Panels, Zähler, Prüfmarke und Speichern folgen mit sculptEnd
    this.history.replace(r.doc, _('pn.sculpt_commit'));
    this.pv = null; this.marks = null; this.sculptOpen = true;
    if (this.v3) this.v3.setDoc(this.doc);
    this.invalidateAll();
    return true;
  };
  /** Ende eines Strichs: holt das nach, was sculptAt während des Strichs ausgelassen hat */
  app.sculptEnd = function () {
    if (!this.sculptOpen) return;
    this.sculptOpen = false;
    this.changed();
  };
  /** Vorschau für die Mausposition: Kanten der neuen Oberfläche (Linienpaare in Karten-Koordinaten); je Mausbewegung bleibt nur die Verschiebung der Eckpunkte im Kreis. */
  app.sculptPreview = function (pos, invert, dir, from) {
    const st = this.sculptStep(pos, invert, dir, from);
    let prep = this.sculptPrep(dir, st), { nz } = displace(prep, st);
    // nichts im Ausschnitt gefunden: Flächen, die nur über einen Umweg außerhalb hängen, brauchen alle Brushes (wie in sculpt); die volle Vorbereitung bleibt bis zur nächsten Änderung
    if (!nz.size && prep.win) {
      const d = dir || 0, f = this._sf2;
      if (!f || f.doc !== this.doc || f.dir !== d || f.only !== this.sculptOpts.onlySel || f.sel !== this.sel || f.vv !== this.visVer) {
        this._sf2 = { doc: this.doc, dir: d, only: this.sculptOpts.onlySel, sel: this.sel, vv: this.visVer, prep: prepare(this.doc, this.sculptScope(), d) };
      }
      prep = this._sf2.prep; nz = displace(prep, st).nz;
    }
    return nz.size ? previewLines(prep, nz) : [];
  };

  app.renderOpts = function () {
    const o = $('#opts'), t = this.tool;
    o.textContent = '';
    const view = this.activeView && this.activeView.ax ? this.activeView : this.v2.top;
    if (t === 'select') {
      o.append(h('span', { class: 'hint', text: _('ui.opts.select') }));
    } else if (t === 'sculpt') {
      const sc = this.sculptOpts;
      const mode = h('select', { class: 'sel', on: { change: (e) => { sc.mode = e.target.value; sc.strength = sc.mode === 'smooth' ? 0.5 : 16; app.renderOpts(); } } },
        [['push', _('pn.sculpt_push')], ['smooth', _('pn.sculpt_smooth')]].map((x) => h('option', { value: x[0], text: x[1], selected: sc.mode === x[0] })));
      o.append(h('span', { class: 'lbl-s', text: _('pn.sculpt_mode') }), mode,
        h('span', { class: 'lbl-s', text: _('pn.sculpt_radius') }), numInput(sc.radius, (v) => { sc.radius = Math.max(16, v); }),
        h('span', { class: 'lbl-s', text: _(sc.mode === 'smooth' ? 'pn.sculpt_smooth_str' : 'pn.sculpt_strength') }), numInput(sc.strength, (v) => { sc.strength = Math.max(0, v); }),
        h('label', { class: 'chk' }, [h('input', { type: 'checkbox', checked: sc.onlySel, on: { change: (e) => { sc.onlySel = e.target.checked; } } }), ' ' + _('pn.sculpt_only_sel')]),
        h('span', { class: 'hint', text: _('ui.opts.sculpt') }));
    } else if (t === 'box') {
      const kind = h('select', { class: 'sel', on: { change: (e) => { app.shape.kind = e.target.value; app.renderOpts(); } } },
        [['box', _('ui.shape.box')], ['wedge', _('ui.shape.wedge')], ['pyramid', _('ui.shape.pyramid')], ['cylinder', _('ui.shape.cylinder')]].map((x) => h('option', { value: x[0], text: x[1], selected: app.shape.kind === x[0] })));
      o.append(h('span', { class: 'lbl-s', text: _('ui.opts.shape') }), kind);
      if (this.shape.kind === 'cylinder') o.append(h('span', { class: 'lbl-s', text: _('ui.opts.sides') }), numInput(this.shape.sides, (v) => { app.shape.sides = Math.max(3, Math.min(32, Math.round(v))); }));
      const ax = view.ax, dl = ['X', 'Y', 'Z'][ax.d], dr = this.depth[ax.d];
      o.append(h('span', { class: 'lbl-s', text: _('ui.opts.depth_from', { axis: dl }) }), numInput(dr[0], (v) => { app.depth[ax.d] = [v, Math.max(v + 1, app.depth[ax.d][1])]; app.renderOpts(); }),
        h('span', { class: 'lbl-s', text: _('ui.opts.to') }), numInput(dr[1], (v) => { app.depth[ax.d] = [Math.min(app.depth[ax.d][0], v - 1), v]; app.renderOpts(); }));
      o.append(h('span', { class: 'hint', text: _('ui.opts.texture', { tex: this.defaultTex() }) + (this.shape.kind === 'wedge' ? ' · ' + _('ui.opts.wedge_hint') : '') }));
    } else if (t === 'entity') {
      const sel = h('select', { class: 'sel', on: { change: (e) => { app.entClass = e.target.value; } } });
      app.panels.fillClassSelect(sel, this.entClass, 'point');
      o.append(h('span', { class: 'lbl-s', text: _('ui.opts.class') }), sel);
      const ax = view.ax, dl = ['X', 'Y', 'Z'][ax.d];
      o.append(h('span', { class: 'lbl-s', text: dl + ' =' }), numInput(this.entDepth[ax.d], (v) => { app.entDepth[ax.d] = v; }));
      o.append(h('span', { class: 'hint', text: _('ui.opts.entity_3d') }));
    } else if (t === 'clip') {
      const keep = h('select', { class: 'sel', on: { change: (e) => { app.clipKeep = e.target.value; app.invalidateAll(); } } },
        [['front', _('ui.clip.front')], ['back', _('ui.clip.back')], ['both', _('ui.clip.both')]].map((x) => h('option', { value: x[0], text: x[1], selected: app.clipKeep === x[0] })));
      o.append(keep,
        h('button', { class: 'btn sm primary', text: _('ui.clip.apply'), disabled: !this.clipLine, on: { click: () => app.applyClip() } }),
        h('button', { class: 'btn sm', text: _('ui.clip.discard'), disabled: !this.clipLine, on: { click: () => app.cancelClip() } }),
        h('span', { class: 'hint', text: _('ui.clip.hint') }));
    } else if (t === 'texture') {
      const T = this.texPaint, mb = (m, key) => h('button', { class: 'btn sm' + (this.texMode === m ? ' primary' : ''), text: _(key), 'aria-pressed': String(this.texMode === m), on: { click: () => app.setTexMode(m) } });
      o.append(mb('edit', 'ui.tex.edit'), mb('paint', 'ui.tex.paint'));
      o.append(h('span', { class: 'lbl-s', text: _('ui.tex.scope') }), h('select', { class: 'sel', 'aria-label': _('ui.tex.scope'), title: _('ui.tex.scope_tip'), on: { change: (e) => { app.setTexScope(e.target.value); } } },
        [['face', _('ui.tex.scope_face')], ['brush', _('ui.tex.scope_brush')], ['group', _('ui.tex.scope_group')]].map((x) => h('option', { value: x[0], text: x[1], selected: T.scope === x[0] }))));
      if (this.texMode === 'paint') {
        o.append(h('label', { class: 'chk', title: _('ui.tex.align_tip') }, h('input', { type: 'checkbox', checked: T.align, on: { change: (e) => { T.align = e.target.checked; } } }), _('ui.tex.align')),
          h('span', { class: 'hint', text: _('ui.tex.paint_with', { tex: this.activeTex }) + (T.src ? ' · ' + _('ui.tex.has_src') : '') }));
      } else o.append(h('span', { class: 'hint', text: _(this.texPaint.scope === 'face' ? 'ui.tex.edit_hint_face' : 'ui.tex.edit_hint') }));
    } else if (t === 'vertex') {
      o.append(h('span', { class: 'hint', text: _('ui.opts.vertex') }));
    }
  };

  // ------------------------------------------------------------ Dateien
  app.loadDoc = function (doc, name, label, warnings, keepBsp) {
    // Server-Karte: nur cloud.open/openRevision verbinden die neue Karte, jedes andere Laden löst die Verbindung
    this.ui.cloud.docLoading();
    if (!keepBsp && this.bsp) { this.bsp = null; if (this.v3) this.v3.setBsp(null); }
    this.resetViewToggles();
    this.skybox.preview = false;   // eine Vorschau gilt nur für die Karte, in der sie gewählt wurde
    this.history.push(doc, label || _('cmd.load'));
    this.sel = Cmd.emptySel(); this.selFace = null; this.clipLine = null;
    this.vis = { manual: new Set(), auto: new Set(), groups: [] }; this.visVer++;
    if (name) this.fileName = name;
    this.light.job = null; this.light.result = null; this.light.on = false; this.light.stale = false;
    if (this.v3) this.v3.setLight(null, false);
    this.updateLightUi();
    this.changed();
    this.ui.cloud.docLoaded();   // das Laden selbst ist kein Grund zum Speichern
    this.ui.chrome();
    this.fit(true);
    if (warnings && warnings.length) this.panels.showWarnings(warnings);
  };
  app.loadBsp = function (buf, name) {
    let b;
    try { b = Bsp.parse(buf); } catch (err) { this.status(_('ui.bsp_unreadable', { msg: err.message })); return; }
    const r = Core.parseText(b.entText);
    const base = name.replace(/\.bsp$/i, '');
    this.bsp = { name: base, data: b, scene: Bsp.scene(b), orig: r.doc, raw: buf };
    this.loadDoc(r.doc, base, _('cmd.load_bsp'), [], true);
    this.light.on = true;
    if (this.v3) this.v3.setBsp(this.bsp.scene);
    this.updateLightUi();
    this.panels.mapLoaded();
    this.panels.showTab('ents');
    const tx = b.textures.filter((t) => t.rgba).length;
    this.status(_('ui.bsp_loaded', { name: name, faces: b.faces.length, ents: r.doc.ents.length - 1, tx: tx, total: b.textures.length }) + (tx < b.textures.length ? ' ' + _('ui.bsp_rest') : '') + (b.lighting.length ? _('ui.bsp_with_lm') : _('ui.bsp_no_lm')));
  };
  app.loadText = function (text, name, label) {
    const r = Core.parseText(text);
    this.loadDoc(r.doc, name.replace(/\.(map|txt)$/i, ''), label || _('cmd.load_file'), r.warnings);
    this.visRestore(text);
    const nb = Core.allBrushes(r.doc).length;
    this.status(_('ui.map_loaded', { name: name || 'Map', count: nb }) + (r.warnings.length ? ', ' + _('ui.map_warnings', { count: r.warnings.length }) : ''));
    this.panels.mapLoaded();
    this.ui.autoLoadWads();
    if (this.assets) void this.assets.autoLoad();
  };
  app.newMap = function () {
    const d = Core.emptyDoc();
    d.ents[0].kv.wad = '\\valve\\halflife.wad;\\valve\\sdhlt.wad';
    this.loadDoc(d, 'neue_map', _('cmd.new_map'));
    this.status(_('ui.new_map'));
    this.ui.autoLoadWads();
    if (this.assets) void this.assets.autoLoad();
  };
  // Dateien aus Drop oder Dateiwahl (Datei oder { file, rel }): Modelle zusammen laden (mit ihren Begleitdateien), FGDs ins Mod-Profil, den Rest einzeln.
  // Bekannte Arten (Sprites, Modelle, Sounds, Skybox-Seiten) gehen danach auf den Server, wenn man angemeldet ist.
  app.openFiles = async function (items) {
    const list = items.map((it) => (it && it.file ? it : { file: it, rel: undefined }));
    const files = list.map((x) => x.file);
    const mdl = files.filter((f) => /\.mdl$/i.test(f.name)), fgd = files.filter((f) => /\.fgd$/i.test(f.name));
    if (mdl.length) await this.addModelFiles(mdl);
    if (fgd.length) await this.addFgdFiles(fgd);
    for (const f of files) if (!/\.(mdl|fgd)$/i.test(f.name)) await this.openFile(f);
    this.ui.uploadDropped(list);
  };
  app.openFile = async function (file) {
    try {
      if (/\.wad$/i.test(file.name)) { await this.addWad(file); return; }
      if (/\.spr$/i.test(file.name)) { await this.addSprite(file); return; }
      if (/\.mdl$/i.test(file.name)) { await this.addModelFiles([file]); return; }
      if (Skybox.nameOf(file.name) && /\.(tga)$/i.test(file.name)) { await this.addSkyFiles([file]); return; }
      if (/\.(wav|mp3|ogg)$/i.test(file.name)) { await this.addSound(file); return; }
      if (/\.bsp$/i.test(file.name)) { this.loadBsp(await file.arrayBuffer(), file.name); return; }
      if (/\.(pts|lin)$/i.test(file.name)) { this.loadLeak(await file.text(), file.name); return; }
      this.loadText(await file.text(), file.name);
    } catch (err) { this.status(_('ui.file_unreadable', { msg: err.message })); }
  };
  // ---- Leak-Anzeige
  app.leak = null;
  app.loadLeak = function (text, name) {
    const pts = Leak.parse(text);
    if (pts.length < 2) { this.status(_('ui.leak_none', { name: name })); return; }
    this.leak = { pts: pts, name: name };
    if (this.v3) this.v3.setSel();
    this.invalidateAll();
    this.panels.leakChanged();
    this.openCheck();
    this.focusPoint(pts[0]);
    this.status(_('ui.leak_loaded', { count: pts.length, len: Math.round(Leak.length(pts)) }));
  };
  app.clearLeak = function () {
    this.leak = null;
    if (this.v3) this.v3.setSel();
    this.invalidateAll(); this.panels.leakChanged();
  };
  // ---- Markierungen aus Prüfbefunden (z. B. überlagerte Flächen): rote Polygone in 3D und 2D
  app.marks = null;
  app.showMarks = function (polys) {
    this.marks = polys && polys.length ? polys : null;
    if (this.v3) this.v3.setSel();
    this.invalidateAll();
    if (this.marks) { const big = this.marks[0], c = big.reduce((a, p) => [a[0] + p[0] / big.length, a[1] + p[1] / big.length, a[2] + p[2] / big.length], [0, 0, 0]); this.focusPoint(c, 160); }
  };
  app.clearMarks = function () {
    if (!this.marks) return;
    this.marks = null;
    if (this.v3) this.v3.setSel();
    this.invalidateAll();
  };
  app.focusPoint = function (p, r) {
    r = r || 192;
    const box = { min: [p[0] - r, p[1] - r, p[2] - r], max: [p[0] + r, p[1] + r, p[2] + r] };
    this.resizeAll();
    Object.keys(this.v2).forEach((k) => this.v2[k].fit(box, 1.0));
    if (this.v3) this.v3.fit(box);
  };
  app.addWad = async function (file) {
    try {
      const w = Wad.parse(await file.arrayBuffer(), file.name);
      this.wads.add(w); this.wadNames.push(file.name + ' (' + w.entries.size + ')');
      if (this.v3) this.v3.texturesChanged();
      this.panels.wadsChanged();
      this.status(_('ui.wad_loaded', { name: file.name, count: w.entries.size }));
      this.ui.syncWad(file);
    } catch (err) { this.status(_('ui.wad_unreadable', { msg: err.message })); }
  };
  // ---- Sprites (.spr): werden an env_sprite/env_glow über das Feld „model“ gebunden
  app.sprites = new Map();
  app.addSprite = async function (file) {
    try {
      const s = Spr.parse(await file.arrayBuffer());
      const c = document.createElement('canvas'); c.width = s.w; c.height = s.h; c.getContext('2d').putImageData(new ImageData(s.rgba, s.w, s.h), 0, 0); s.canvas = c;
      this.sprites.set(Spr.key(file.name), s);
      if (this.v3) this.v3.texturesChanged();
      if (this.v2) this.invalidateAll();
      this.panels.wadsChanged();
      this.status(_('ui.sprite_loaded', { name: file.name, w: s.w, h: s.h, count: s.count, format: s.formatName, type: s.typeName }));
    } catch (err) { this.status(_('ui.sprite_unreadable', { name: file.name, msg: err.message })); }
  };
  // ---- Modelle (.mdl): Entities mit studio() in der FGD zeigen ihr Modell statt der Box; Texturen stehen in nameT.mdl
  const MDL_ERR = { short: 'mdl.e_short', not_mdl: 'mdl.e_not', version: 'mdl.e_version', range: 'mdl.e_range' };   // Fehlercodes des MDL-Moduls → Texte
  app.models = new ModelStore();
  app.modelKeys = function () { return this.models.keys(); };
  /** Modell-Dateien laden (zusammen mit Begleitdateien); mehrere Dateien auf einmal, damit die Reihenfolge keine Rolle spielt */
  app.addModelFiles = async function (files) {
    try {
      const list = await Promise.all(Array.from(files).map(async (f) => ({ name: f.name, buf: await f.arrayBuffer() })));
      const ok = this.models.add(list);
      for (const k of ok) {
        const m = this.models.get(k);
        if (m.needsTextureFile && !m.textures.length) this.status(_('ui.model_notex', { name: k, tex: k.replace(/\.mdl$/i, 't.mdl') }));
        else this.status(_('ui.model_loaded', { name: k, verts: m.bodyparts.reduce((a, bp) => a + bp.models.reduce((b, sm) => b + sm.vertBone.length, 0), 0), tex: m.textures.length }));
      }
      for (const f of list) {
        const err = this.models.errors.get(f.name.replace(/^.*[\\/]/, '').toLowerCase());
        if (err) this.status(_('ui.model_unreadable', { name: f.name, msg: MDL_ERR[err.code] ? _(MDL_ERR[err.code], { ver: err.detail }) : err.message }));
      }
      if (this.v3) this.v3.texturesChanged();
      this.updateSkyUi();
      this.panels.wadsChanged();
    } catch (err) { this.status(_('ui.model_unreadable', { name: '', msg: err.message })); }
  };
  // ---- Sounds (Vorschau von ambient_generic)
  app.sounds = new Map(); app.soundPlaying = new Map(); app.spatial = false; app.audioCtx = null;
  app.soundKey = soundKey;   // Pfad ab sound/, klein (siehe net/sounds.ts)
  // ---- Skybox: env/<name>{rt,lf,up,dn,ft,bk}.tga laden, Würfeltextur für die 3D-Ansicht
  app.skybox = { name: '', imgs: {}, mats: null, on: true, rot: { up: 0, dn: 0 }, preview: false };
  // opts.preview: Skybox ausdrücklich zur Ansicht zeigen, auch wenn sie nicht zum skyname der Karte passt (Standard: ja, z. B. beim Ablegen von Dateien)
  app.addSkyFiles = async function (files, opts) {
    let n = 0;
    for (const f of files) {
      const nm = Skybox.nameOf(f.name); if (!nm) { this.status(_('ui.sky_badname', { name: f.name })); continue; }
      try {
        let im;
        if (/\.tga$/i.test(f.name)) im = Skybox.parseTga(await f.arrayBuffer());
        else { const bmp = await createImageBitmap(f), c = document.createElement('canvas'); c.width = bmp.width; c.height = bmp.height; const cx = c.getContext('2d'); cx.drawImage(bmp, 0, 0); im = { w: c.width, h: c.height, rgba: cx.getImageData(0, 0, c.width, c.height).data }; }
        if (this.skybox.name !== nm.name) this.skybox = { name: nm.name, imgs: {}, mats: null, on: true, rot: { up: 0, dn: 0 }, preview: false };
        this.skybox.imgs[nm.side] = im; n++;
      } catch (err) { this.status(_('ui.sky_unreadable', { name: f.name, msg: err.message })); }
    }
    if (n) {
      this.skybox.preview = !opts || opts.preview === undefined ? true : !!opts.preview;
      if (this.skybox.preview) this.skybox.on = true;   // eine gewünschte Vorschau soll auch zu sehen sein, selbst wenn „In 3D zeigen“ vorher aus war
      this.buildSky();
    }
  };
  app.setSkyPreview = function (on) {
    this.skybox.preview = !!on;
    if (on) { this.skybox.on = true; this.status(_('ui.sky_preview', { name: this.skybox.name })); }
    if (this.v3) this.v3.skyboxChanged();
    this.panels.wadsChanged();
    this.scheduleCheckFlag();
  };
  app.buildSky = function () {
    const sb = this.skybox, have = Skybox.SIDES.filter((s) => sb.imgs[s]);
    if (have.length < 6) { this.status(_('ui.sky_partial', { name: sb.name, have: have.length, missing: Skybox.SIDES.filter((s) => !sb.imgs[s]).join(', ') })); this.panels.wadsChanged(); return; }
    const faces = Skybox.toCube(sb.imgs, sb.rot).map((f) => { const c = document.createElement('canvas'); c.width = f.w; c.height = f.h; c.getContext('2d').putImageData(new ImageData(f.rgba, f.w, f.h), 0, 0); return c; });
    if (sb.mats) sb.mats.forEach((m) => { m.map.dispose(); m.dispose(); });
    sb.mats = faces.map((c) => { const t = new THREE.CanvasTexture(c); t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter; t.generateMipmaps = false; return new THREE.MeshBasicMaterial({ map: t, side: THREE.BackSide, depthWrite: false, depthTest: false, fog: false }); });
    this.status(_('ui.sky_loaded', { name: sb.name, px: faces[0].width }));
    this.panels.wadsChanged(); if (this.v3) this.v3.skyboxChanged();
  };
  app.setSkyRot = function (side, k) { this.skybox.rot[side] = k; this.buildSky(); };
  app.setSkyOn = function (on) { this.skybox.on = !!on; if (this.v3) this.v3.skyboxChanged(); };
  app.getAudio = function () {
    if (!this.audioCtx) { const AC = window.AudioContext || window.webkitAudioContext; if (!AC) return null; this.audioCtx = new AC(); }
    if (this.audioCtx.state === 'suspended') this.audioCtx.resume();
    return this.audioCtx;
  };
  app.addSound = async function (file, key) {
    const ac = this.getAudio(); if (!ac) { this.status(_('ui.no_audio')); return; }
    try {
      const buf = await ac.decodeAudioData(await file.arrayBuffer());
      this.sounds.set(key ? soundKey(key) : soundKey(file.name), { name: file.name, buf: buf, dur: buf.duration });
      this.panels.wadsChanged();
      this.status(_('ui.sound_loaded', { name: file.name, secs: buf.duration.toFixed(1), count: buf.numberOfChannels, hz: buf.sampleRate }));
    } catch (err) { this.status(_('ui.sound_unreadable', { name: file.name, msg: err.message })); }
  };
  app.soundFor = function (e) { return e && e.kv && e.kv.classname === 'ambient_generic' ? lookupSound(this.sounds, soundKey(e.kv.message)) || null : null; };
  app.hasSound = function (key) { return lookupSound(this.sounds, key) !== undefined; };
  app.soundVol = function (e) { const v = parseFloat(e.kv.health); return Math.max(0, Math.min(1, (isFinite(v) ? v : 10) / 10)); };
  app.stopSound = function (e) { const p = this.soundPlaying.get(e.id); if (p) { try { p.src.stop(); } catch (x) { /* schon beendet */ } this.soundPlaying.delete(e.id); } };
  app.playSound = function (e, gain) {
    const snd = this.soundFor(e), ac = this.getAudio(); if (!snd || !ac) return false;
    this.stopSound(e);
    const src = ac.createBufferSource(), g = ac.createGain(), pt = parseFloat(e.kv.pitch);
    src.buffer = snd.buf; src.loop = !((parseInt(e.kv.spawnflags, 10) || 0) & 32); src.playbackRate.value = (isFinite(pt) && pt > 0 ? pt : 100) / 100;
    g.gain.value = gain === undefined ? this.soundVol(e) : gain;
    src.connect(g); g.connect(ac.destination); src.start();
    src.onended = () => { const p = this.soundPlaying.get(e.id); if (p && p.src === src) { this.soundPlaying.delete(e.id); this.panels.selChanged(true); } };
    this.soundPlaying.set(e.id, { src: src, gain: g });
    return true;
  };
  app.stopAllSounds = function () { Array.from(this.soundPlaying.keys()).forEach((id) => { const p = this.soundPlaying.get(id); try { p.src.stop(); } catch (x) { /* egal */ } }); this.soundPlaying.clear(); };
  /** Raumklang: Lautstärke aller geladenen ambient_generic nach Abstand der 3D-Kamera zum Hörradius. */
  app.setSpatial = function (on) {
    this.spatial = !!on;
    clearInterval(this.spatialTimer);
    if (!on) { this.stopAllSounds(); this.panels.wadsChanged(); return; }
    const tick = () => {
      const v3 = this.v3; if (!this.spatial || !v3 || !v3.ok) return;
      const cp = v3.camera.position, cam = [cp.x, -cp.z, cp.y];     // three.js (x,y,z) → Map (x, −z, y)
      this.doc.ents.forEach((e) => {
        if (e.kv.classname !== 'ambient_generic' || !this.soundFor(e)) return;
        const o = Core.parseVec(e.kv.origin); if (!o) return;
        const rad = EntDefs.soundRadius(e.kv), d = Math.hypot(o[0] - cam[0], o[1] - cam[1], o[2] - cam[2]);
        const att = rad === 0 ? 1 : d >= rad ? 0 : Math.pow(1 - d / rad, 1.5), gain = this.soundVol(e) * att;
        let p = this.soundPlaying.get(e.id);
        if (!p) { if (gain <= 0.001) return; this.playSound(e, 0); p = this.soundPlaying.get(e.id); }
        if (p) p.gain.gain.setTargetAtTime(gain, this.audioCtx.currentTime, 0.08);
      });
    };
    this.getAudio(); tick(); this.spatialTimer = setInterval(tick, 150);
  };
  app.spriteFor = function (e) {
    if (!e || !e.kv) return null;
    const path = spritePathOf(e);
    return path ? this.sprites.get(Spr.key(path)) || null : null;
  };
  // Mod-Profil: FGD-Dateien der Karte (Schlüssel xf_fgd). Die Klassenliste wird neu gesetzt, sobald sich die Liste oder ein Text ändert.
  app.syncFgd = function () {
    const world = this.doc && this.doc.ents[0];
    if (!world) return false;
    // Ohne gespeichertes Mod-Profil gilt die Vorgabe nach dem Kartennamen (op4_ → Opposing Force, de_/cs_ → Counter-Strike)
    const eff = effectiveFgds(world.kv[FGD_KEY], this.fileName, isBundled), names = eff.names;
    const key = names.join(';') + '|' + eff.auto + '|' + this.fgdVer;
    if (key === this.fgdKey) return false;
    this.fgdKey = key; this.fgdIsAuto = eff.auto;
    this.fgdState = applyFgds(names, this.fgdTexts);
    // Fehlende, aber mitgelieferte FGDs nachladen (eine hochgeladene Datei gleichen Namens hat Vorrang)
    for (const n of this.fgdState.missing.concat(this.fgdState.includeMissing || [])) {   // auch per @include verlangte Dateien
      const k = n.toLowerCase();
      if (!isBundled(n) || this.fgdPending.has(k)) continue;
      this.fgdPending.add(k);
      bundledText(n).then((text) => { if (text !== null && !this.fgdTexts.has(k)) this.addFgdText(n, text); }).catch(() => {}).finally(() => this.fgdPending.delete(k));
    }
    return true;
  };
  app.bundledFgds = function () { return bundledNames(); };
  app.addBundledFgd = async function (name) {
    const text = await bundledText(name);
    if (text === null) return;
    this.addFgdText(name, text);
    const kv = this.doc.ents[0].kv[FGD_KEY] || '', next = addToProfile(kv, this.fgdAuto(), name);
    if (next !== kv) this.commit(Cmd.setKV(this.doc, this.doc.ents[0].id, FGD_KEY, next), _('cmd.fgd_add'));
    this.status(_('ui.fgd_loaded', { count: this.fgdState.classes }));
  };
  app.fgdStatus = function () { return this.fgdState; };
  /** Keine Spiel-FGD verknüpft (weder gespeichert noch nach Kartenname; die FGD der Compiler-Werkzeuge zählt nicht): es gilt nur die eingebaute Klassenliste */
  app.fgdNone = function () { return !this.bsp && !this.fgdState.loaded.concat(this.fgdState.missing).some((n) => !COMPILER_FGDS.includes(n.toLowerCase())); };
  /** Dateien der Vorgabe nach Kartenname, solange die Karte keine eigenen hat */
  app.fgdAuto = function () { return this.fgdIsAuto ? this.fgdState.loaded.concat(this.fgdState.missing) : []; };
  // Text einer FGD einsetzen (vom Server oder aus einer Datei) und alles neu zeichnen, was Klassen nutzt
  app.addFgdText = function (name, text) {
    this.fgdTexts.set(name.toLowerCase(), text);
    this.fgdVer++;
    if (this.syncFgd()) { if (this.v3) this.v3.setDoc(this.doc); this.invalidateAll(); this.panels.docChanged(); this.autoLoadAssets(); }
  };
  // Fehlende Sprites, Modelle und die Skybox vom Server holen (nur angemeldet; gleichzeitige Aufrufe werden zusammengefasst)
  app.autoLoadAssets = function () { if (this.assets) void this.assets.autoLoad(); };
  app.addFgdFiles = async function (files) {
    const names = [];
    for (const f of files) {
      if (!/\.fgd$/i.test(f.name)) { this.status(_('ui.fgd_not_fgd', { name: f.name })); continue; }
      try { this.addFgdText(f.name, await f.text()); names.push(f.name); this.ui.syncFgd(f); }
      catch (err) { this.status(_('ui.fgd_unreadable', { name: f.name, msg: err.message })); }
    }
    if (!names.length) return;
    const kv = this.doc.ents[0].kv[FGD_KEY];
    const auto = this.fgdAuto(), next = names.reduce((acc, n) => addToProfile(acc, acc ? [] : auto, n), kv || '');
    if (next !== (kv || '')) this.commit(Cmd.setKV(this.doc, this.doc.ents[0].id, FGD_KEY, next), _('cmd.fgd_add'));
    const st = this.fgdState;
    this.status(_('ui.fgd_loaded', { count: st.classes }));
  };
  app.clearWads = function () {
    this.wads.clear(); this.wadNames = [];
    if (this.v3) this.v3.texturesChanged();
    this.panels.wadsChanged();
  };

  // ------------------------------------------------------------ Export
  app.exportText = function () { return Core.toText(this.doc) + this.visText(); };
  // ------------------------------------------------------------ Suchen / Auswählen / Ersetzen (Strg+F)
  app.findState = app.findState || newFindState();
  app.openCheck = function () { this.panels.checkRes = null; openDialog((close) => createElement(CheckDialog, { app: this, shared: this.panels, onClose: () => close() })).then(() => this.scheduleCheckFlag(0)); };
  // Markierung am Prüfen-Knopf: schwerste Stufe der Prüfung, im Hintergrund nach Änderungen aktualisiert
  app.scheduleCheckFlag = function (delay) {
    clearTimeout(this.checkFlagT);
    this.checkFlagT = setTimeout(() => this.updateCheckFlag(), delay === undefined ? 1500 : delay);
  };
  app.updateCheckFlag = function () {
    const btn = $('#b-check'), flag = $('#b-check-flag');
    if (!btn || !flag) return;
    let w = { level: null, err: 0, warn: 0 };
    if (!this.bsp) {
      try { w = Core.worstLevel(Core.validate(this.doc, { hasTex: this.wads.count() ? (t) => this.wads.has(t) : undefined, texSize: this.wads.count() ? (t) => this.wads.size(t) : undefined })); } catch (e) { /* Prüfung nicht möglich: keine Markierung */ }
    }
    if (w.level) btn.dataset.level = w.level; else delete btn.dataset.level;
    flag.hidden = !w.level;
    flag.title = w.level ? _('app.check.flag', { err: w.err, warn: w.warn }) : '';
  };
  app.openFind = function () { openDialog((close) => createElement(FindDialog, { app: this, state: this.findState, onClose: () => close() })); };
  // ------------------------------------------------------------ Settings und Server (React-Dialoge, siehe ui/backend-ui.tsx)
  app.settingsTab = 'design';
  app.openTheme = function () { this.openSettings('design'); };
  app.openSettings = function (tab) { this.ui.openSettings(tab || this.settingsTab); };
  app.openCloud = function () { return this.ui.openCloud(); };
  app.openSave = function () { return this.ui.openSave(); };
  // „Öffnen“: Dialog mit den Karten auf dem Server (angemeldet), „Von Datei …“ und den Beispielen
  app.openMap = function () { return this.ui.openCloud(); };
  app.closeModal = function () { closeAllDialogs(); };
  /** Esc und Klick auf den Hintergrund: schließt den obersten Dialog */
  app.closeTopModal = function () { closeTop(); };
  app.openExport = async function () {
    if (this.bsp) { return this.openBspExport(); }
    const doc = this.doc;
    const res = Core.validate(doc, { hasTex: this.wads.count() ? (t) => this.wads.has(t) : undefined, texSize: this.wads.count() ? (t) => this.wads.size(t) : undefined, skipLeak: true });
    const info = { brushes: Core.allBrushes(doc).length, ents: doc.ents.length - 1, errors: res.filter((r) => r.level === 'err').map((r) => r.text), warnings: res.filter((r) => r.level === 'warn').map((r) => r.text) };
    const dl = await getDownloads(), text = this.exportText(), base = safeName(this.fileName);
    openDialog((close) => createElement(ExportDialog, { text: text, base: base, info: info, dl: dl, onClose: () => close() }));
  };

  /** Texturen einer geladenen BSP tauschen: alle Miptex-Einträge mit dem Namen `from` bekommen `to`. */
  app.bspReplaceTex = function (from, to) {
    const B = this.bsp; if (!B) return;
    to = String(to || '').trim(); if (to.length > 15) { this.status(_('bsp.name_max')); return; }
    B.repl = B.repl || {};
    const sz = to && this.wads.size(to);
    let n = 0;
    B.data.textures.forEach((t, i) => {
      if (t.name.toUpperCase() !== from.toUpperCase()) return;
      if (t.orig === undefined) t.orig = { name: t.name, w: t.w, h: t.h, rgba: t.rgba };
      if (!to || to.toUpperCase() === t.orig.name.toUpperCase()) { // zurück zum Original
        t.name = t.orig.name; t.w = t.orig.w; t.h = t.orig.h; t.rgba = t.orig.rgba; delete B.repl[i];
      } else {
        t.name = to; t.rgba = null; if (sz) { t.w = sz.w; t.h = sz.h; }
        B.repl[i] = { name: to, w: t.w, h: t.h };
      }
      n++;
    });
    if (!n) return;
    B.scene = Bsp.scene(B.data);
    if (this.v3) this.v3.setBsp(B.scene);
    if (this.panels) this.panels.render();
    this.status(from + ' → ' + (to || _('bsp.original')) + (to && !sz ? ' ' + _('bsp.no_wad') : ''));
  };

  /** BSP in Brushes umwandeln (Modul Decomp) und als bearbeitbare Map öffnen. Die aktuell bearbeiteten Entities bleiben erhalten. */
  app.decompileBsp = function () {
    const B = this.bsp; if (!B) return;
    let r;
    try { r = decompileBsp(B.data.buf, { entText: Bsp.entsToText(this.doc), nullTex: (this.decompNull || 'NULL') }); } catch (err) { this.status(_('bsp.decomp_failed', { msg: err.message })); return; }
    const name = (B.name || 'map.bsp').replace(/\.bsp$/i, '') + '_dekompiliert.map';
    this.loadText(r.mapText, name, _('cmd.decompile'));
    this.status(_('bsp.decomp_done', { brushes: r.stats.brushes, tex: r.stats.texturedFaces, faces: r.stats.faces }) + ' ' + r.warnings.join(' '));
  };

  app.openBspExport = async function () {
    const doc = this.doc, B = this.bsp;
    const dl = await getDownloads(), base = safeName(B.name), text = Bsp.entsToText(doc);
    openDialog((close) => createElement(BspExportDialog, {
      base: base, entText: text, changed: text !== Bsp.entsToText(B.orig), entCount: doc.ents.length, texSwapped: !!(B.repl && Object.keys(B.repl).length), dl: dl,
      makeZip: () => Bsp.zip([{ name: base + '.bsp', data: Bsp.withEntities(B.data, text, B.repl) }]), onClose: () => close()
    }));
  };

  // ------------------------------------------------------------ Kontextmenü (Rechtsklick)
  const keyOf = (id) => { const c = Keys.get(id); return c.length ? Keys.fmt(c[0]) : ''; };
  const snapV = (v) => { const g = app.snapOn ? app.grid : 1; return Math.round(v / g) * g; };
  const texOfBrush = (b) => { const n = {}; let best = null, bc = 0; b.faces.forEach((f) => { n[f.tex] = (n[f.tex] || 0) + 1; if (n[f.tex] > bc) { bc = n[f.tex]; best = f.tex; } }); return best; };
  const sameTex = (a, b) => String(a).toUpperCase() === String(b).toUpperCase();

  /** Mittelpunkt der Zwischenablage (Brushes und Entity-Ursprünge) */
  app.clipCenter = function () {
    const c = this.clipboard; if (!c) return null;
    const bs = [], pts = [];
    c.groups.forEach((g) => g.brushes.forEach((b) => bs.push(b)));
    c.ents.forEach((e) => { const o = Core.parseVec(e.kv.origin); if (o) pts.push(o); });
    const bb = bs.length ? Core.bboxOf(bs) : (pts.length ? { min: pts[0].slice(), max: pts[0].slice() } : null);
    if (!bb) return null;
    pts.forEach((o) => { for (let i = 0; i < 3; i++) { bb.min[i] = Math.min(bb.min[i], o[i]); bb.max[i] = Math.max(bb.max[i], o[i]); } });
    return [0, 1, 2].map((i) => (bb.min[i] + bb.max[i]) / 2);
  };
  /** Einfügen so, dass der Mittelpunkt der Zwischenablage bei `pos` liegt (null-Achsen bleiben unverändert) */
  app.pasteAt = function (pos) {
    const c = this.clipCenter(); if (!c) return;
    const g = this.snapOn ? this.grid : 1, off = [0, 0, 0];
    for (let i = 0; i < 3; i++) off[i] = pos[i] === null || pos[i] === undefined ? 0 : Math.round((pos[i] - c[i]) / g) * g;
    const r = Cmd.paste(this.doc, this.clipboard, off);
    this.commit(r.doc, _('cmd.paste'), r.sel);
  };

  /** Menüeinträge für eine Stelle. info = { hit:{type,id}|null, face:int|null, pos:[x,y,z|null]|null } */
  app.ctxItems = function (info) {
    const it = [], sel = this.sel, doc = this.doc, hit = info.hit, A = (t, id, extra) => Object.assign({ t: t, key: keyOf(id), run: () => runAction(id) }, extra || {});
    if (hasSel()) {
      const nB = sel.b.size, nE = sel.e.size, ids = Array.from(sel.b).concat(Array.from(sel.e));
      let head = _('ctx.objects', { count: nB + nE });
      if (nB + nE === 1) {
        if (nE) { const en = doc.ents.find((e) => e.id === ids[0]); head = en ? en.kv.classname : _('ctx.entity'); } else { const en = Core.entityOfBrush(doc, ids[0]); head = en && en.kv.classname !== 'worldspawn' ? _('ctx.brush_in', { cls: en.kv.classname }) : _('ctx.brush'); }
      }
      it.push({ hdr: head });
      it.push(A(_('ctx.copy'), 'copy'), A(_('ctx.cut'), 'cut'), A(_('cmd.duplicate'), 'dup'), A(_('cmd.delete'), 'del', { danger: true }), { sep: true });
      const grouped = this.vis.groups.some((g) => ids.some((id) => g.members.has(id)));
      it.push(A(_('ctx.group'), 'group', { dis: nB + nE < 2, tip: nB + nE < 2 ? _('ctx.group_tip') : '' }));
      if (grouped) it.push(A(_('ctx.ungroup'), 'ungroup'));
      it.push(A(_('ctx.hide'), 'hide'), A(_('ctx.isolate'), 'isolate'), { sep: true });
      if (nB) it.push(A(_('cmd.carve'), 'carve'));
      if (nB > 1) it.push(A(_('cmd.merge'), 'merge'));
      if (nB) {
        const mk = (g) => EntDefs.brushClasses().filter((d) => d.g === g).map((d) => ({ t: d.c, run: () => this.toEntity(d.c) }));
        it.push({ t: _('ctx.to_entity'), sub: [{ hdr: _('ctx.function') }].concat(mk('func'), [{ sep: true }, { hdr: _('ctx.trigger') }], mk('trigger')) });
        if (doc.ents.some((e, i) => i > 0 && e.brushes.some((b) => sel.b.has(b.id)))) it.push({ t: _('ctx.to_world'), run: () => this.toWorld() });
        // Texturen
        let picked = null;
        if (info.face !== null && info.face !== undefined && hit && hit.type === 'b') { const fb = Core.findBrush(doc, hit.id); if (fb && fb.b.faces[info.face]) picked = fb.b.faces[info.face].tex; }
        if (!picked && hit && hit.type === 'b') { const fb = Core.findBrush(doc, hit.id); if (fb) picked = texOfBrush(fb.b); }
        const act = this.defaultTex(), ts = [{ t: _('ctx.apply_tex', { tex: act }), run: () => this.applyTex(act) }];
        if (picked) {
          ts.push({ t: _('ctx.pick_tex', { tex: picked }), run: () => { this.setActiveTex(picked, false); this.status(_('ctx.active_tex', { tex: picked })); } },
            { t: _('ctx.replace_tex'), run: () => { this.findState.tex = picked; this.openFind(); } },
            { sep: true },
            { t: _('ctx.select_brushes_tex', { tex: picked }), run: () => {
              const ns = Cmd.emptySel(), hid = this.hiddenSet();
              Core.allBrushes(this.viewDoc()).forEach((x) => { if (!hid.has(x.b.id) && x.b.faces.some((f) => sameTex(f.tex, picked))) ns.b.add(x.b.id); });
              this.setSel(ns); this.status(_('ctx.brushes_with', { count: ns.b.size, tex: picked }));
            } });
          if (info.face !== null && info.face !== undefined) ts.push({ t: _('ctx.select_faces_tex', { tex: picked }), run: () => {
            const fl = []; Core.allBrushes(this.viewDoc()).forEach((x) => x.b.faces.forEach((f, i) => { if (sameTex(f.tex, picked)) fl.push({ b: x.b.id, f: i }); }));
            this.setFaceSel(fl); this.status(_('ctx.faces_with', { count: fl.length, tex: picked }));
          } });
        }
        it.push({ t: _('cmd.texture'), sub: ts });
      }
      it.push({ t: _('ctx.transform'), sub: [A(_('ctx.rot_cw'), 'rotate'), A(_('ctx.rot_ccw'), 'rotateBack'), { sep: true }, { t: _('ctx.mirror_x'), run: () => this.mirror(0) }, { t: _('ctx.mirror_y'), run: () => this.mirror(1) }, { t: _('ctx.mirror_z'), run: () => this.mirror(2) }] });
      it.push({ sep: true }, { t: _('ctx.properties'), run: () => { this.panels.showTab('props'); } }, A(_('find.show_sel'), 'fit'), { sep: true });
    }
    // Stelle unter dem Mauszeiger
    if (info.pos) {
      const o = [0, 1, 2].map((i) => (info.pos[i] === null || info.pos[i] === undefined ? this.entDepth[i] : snapV(info.pos[i])));
      const mk = (g) => EntDefs.pointClasses().filter((d) => d.g === g).map((d) => ({ t: d.c, run: () => this.placeEntity(o, d.c) }));
      const es = [];
      if (this.entClass && !EntDefs.isBrushClass(this.entClass)) es.push({ hdr: _('ctx.recent') }, { t: this.entClass, run: () => this.placeEntity(o, this.entClass) }, { sep: true });
      [['player', _('ctx.g_player')], ['light', _('ctx.g_light')], ['item', _('ctx.g_item')], ['monster', _('ctx.g_monster')], ['other', _('ctx.g_other')]].forEach((g) => es.push({ t: g[1], sub: mk(g[0]) }));
      it.push({ t: _('ctx.place_here'), sub: es });
      it.push(A(_('ctx.paste_here'), 'paste', { dis: !this.clipboard, run: () => this.pasteAt(info.pos), tip: this.clipboard ? '' : _('ctx.copy_first') }));
    } else it.push(A(_('cmd.paste'), 'paste', { dis: !this.clipboard }));
    it.push(A(_('ctx.select_all'), 'selectAll'));
    if (this.hiddenSet().size) it.push(A(_('ctx.show_all'), 'showAll'));
    it.push(A(_('ctx.fit_all'), 'fitAll'), { sep: true });
    it.push({ t: _('app.grid'), sub: GRIDS.map((g) => ({ t: String(g), chk: g === this.grid, run: () => this.setGrid(g) })).concat([{ sep: true }, A(_('ctx.snap'), 'snap', { chk: this.snapOn }), A(_('app.lock'), 'texLock', { chk: this.lock })]) });
    return it;
  };
  /** Rechtsklick (oder Tastatur) in einer Ansicht: Objekt unter dem Zeiger wählen und Menü öffnen */
  app.contextAt = function (view, e, kbd) {
    const is3 = view === this.v3;
    let hit = null, pos = null, face = null;
    if (is3) {
      if (!view.ok) return;
      const r = view.rayHit(e.clientX, e.clientY, false);
      if (r.best) { hit = { type: r.best.type, id: r.best.id }; face = r.best.face === undefined ? null : r.best.face; }
      pos = r.pos;
    } else {
      const p = view.pos(e), all = view.pickAll(p.x, p.y), w = view.toWorld(p.x, p.y);
      hit = Cmd.pickHit(all, this.sel, false); // ein schon gewähltes Element unter dem Zeiger behält die Auswahl
      pos = [0, 0, 0]; pos[view.ax.h] = w[0]; pos[view.ax.v] = w[1]; pos[view.ax.d] = null;
    }
    if (hit) {
      const set = hit.type === 'b' ? this.sel.b : this.sel.e;
      if (!set.has(hit.id)) { const ns = Cmd.emptySel(); (hit.type === 'b' ? ns.b : ns.e).add(hit.id); this.selFace = null; this.setSel(ns); }
    }
    this.activeView = view;
    CtxMenu.open(this.ctxItems({ hit: hit, face: face, pos: pos }), e.clientX, e.clientY, { kbd: !!kbd });
  };
  /** Menü per Tastatur: am letzten Mauszeiger in der aktiven Ansicht, sonst in deren Mitte */
  app.contextAtCursor = function () {
    const v = this.activeView || this.v2.top; if (!v || !v.canvas) return;
    const r = v.canvas.getBoundingClientRect(), p = this.ptr && this.ptr.view === v ? this.ptr : null;
    this.contextAt(v, { clientX: p ? p.x : r.left + r.width / 2, clientY: p ? p.y : r.top + r.height / 2, altKey: false }, true);
  };

  // ------------------------------------------------------------ Tastatur: alle Kürzel laufen über Keys (Settings → Tastenkürzel)
  const viewAx = () => (app.activeView && app.activeView.ax ? app.activeView.ax : AX.top);
  const ACT = {
    new: () => app.newMap(), open: () => app.openMap(), export: () => app.openExport(), settings: () => app.openSettings('design'), help: () => app.openSettings('keys'),
    undo: () => app.undo(), redo: () => app.redo(), cut: () => { app.copy(); app.del(); }, copy: () => app.copy(), paste: () => app.paste(), dup: () => app.dup(), del: () => app.del(),
    selectAll: () => app.selectAll(), find: () => app.openFind(), rotate: () => app.rotate90(viewAx().d, 1), rotateBack: () => app.rotate90(viewAx().d, -1),
    cancel: () => { if (app.clipLine) app.cancelClip(); else if (app.tool !== 'select') app.setTool('select'); else app.setSel(Cmd.emptySel()); },
    confirm: () => { if (app.tool !== 'clip') return false; app.applyClip(); return true; },
    group: () => app.groupSel(), ungroup: () => app.ungroupSel(), carve: () => app.carve(), merge: () => app.merge(), hide: () => app.hideSel(), isolate: () => app.isolateSel(), showAll: () => app.showAll(),
    toolTexture: () => app.setTool('texture'), toolSelect: () => app.setTool('select'), toolBox: () => app.setTool('box'), toolEntity: () => app.setTool('entity'), toolClip: () => app.setTool('clip'), toolVertex: () => app.setTool('vertex'), toolSculpt: () => app.setTool('sculpt'),
    snap: () => $('#c-snap').click(), texLock: () => $('#c-lock').click(),
    gridDown: () => app.setGrid(Math.max(1, app.grid / 2)), gridUp: () => app.setGrid(Math.min(256, app.grid * 2)),
    view3d: () => app.setLayout('single', 'persp'), viewTop: () => app.setLayout('single', 'top'), viewFront: () => app.setLayout('single', 'front'), viewSide: () => app.setLayout('single', 'side'), viewQuad: () => app.setLayout('quad'),
    fit: () => app.fit(false), fitAll: () => app.fit(true), aids: () => $('#c-aids').click(),
    light: () => { if (!$('#b-light').disabled) app.toggleLight(); }, fly: () => { if (!$('#b-fly').disabled) app.toggleFly(); },
    context: () => app.contextAtCursor()
  };
  function runAction(id) { const f = ACT[id]; return f ? f() : false; }
  app.runAction = runAction;

  /** Tooltips der Leiste zeigen das aktuell gültige Kürzel */
  const HINTS = [['#b-new', 'new'], ['#b-open', 'open'], ['#b-export', 'export'], ['#b-find', 'find'], ['#b-settings', 'settings'], ['#b-undo', 'undo'], ['#b-redo', 'redo'], ['#b-fit', 'fit'], ['#b-quad', 'viewQuad'], ['#b-fly', 'fly'], ['#b-light', 'light'],
    ['#c-snap', 'snap', 1], ['#c-lock', 'texLock', 1], ['#c-aids', 'aids', 1], ['#vtabs [data-v="persp"]', 'view3d'], ['#vtabs [data-v="top"]', 'viewTop'], ['#vtabs [data-v="front"]', 'viewFront'], ['#vtabs [data-v="side"]', 'viewSide']];
  app.refreshHints = function () {
    HINTS.forEach((x) => {
      let el = $(x[0]); if (!el) return; if (x[2]) el = el.parentElement;
      const tk = el.getAttribute('data-i18n-title');
      el.dataset.t0 = (tk ? _(tk) : (el.title || '')).replace(/\s*\(((Strg|Ctrl)\+\w+|[A-Z])\)/, '');
      const k = Keys.fmtAll(x[1]);
      el.title = (el.dataset.t0 || '') + (k ? (el.dataset.t0 ? ' ' : '') + '(' + k + ')' : '');
    });
    $$('#tools .tool').forEach((b) => {
      const id = { select: 'toolSelect', box: 'toolBox', entity: 'toolEntity', clip: 'toolClip', texture: 'toolTexture', vertex: 'toolVertex', sculpt: 'toolSculpt' }[b.dataset.tool], kb = b.querySelector('kbd'), t = TOOLS.find((x) => x.k === b.dataset.tool);
      if (!id || !t) return;
      const c = Keys.get(id); if (kb) kb.textContent = c.length ? Keys.fmt(c[0]) : '';
      b.title = _(t.n) + (c.length ? ' (' + Keys.fmtAll(id) + ')' : '');
    });
  };

  function typing(e) { const t = e.target; return t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable); }
  function onKey(e) {
    if (CtxMenu.isOpen()) return;
    if (!$('#modal').hidden) { if (e.key === 'Escape') app.closeTopModal(); return; }
    if (typing(e)) { if (e.key === 'Escape') e.target.blur(); return; }
    const k = e.key, mod = e.ctrlKey || e.metaKey, kl = k.toLowerCase();
    if (k === ' ') { app.spaceDown = true; e.preventDefault(); return; }
    if (app.v3 && app.v3.captures(kl) && !mod) return;
    if (!mod && (k.indexOf('Arrow') === 0 || k === 'PageUp' || k === 'PageDown')) {
      if (!hasSel()) return;
      const v = viewAx(), g = app.snapOn ? app.grid : 1, d = [0, 0, 0], m = e.shiftKey ? 8 : 1;
      if (app.activeView === app.v3) { if (k === 'ArrowLeft') d[0] = -g * m; if (k === 'ArrowRight') d[0] = g * m; if (k === 'ArrowUp') d[1] = g * m; if (k === 'ArrowDown') d[1] = -g * m; if (k === 'PageUp') d[2] = g * m; if (k === 'PageDown') d[2] = -g * m; } else {
        if (k === 'ArrowLeft') d[v.h] = -g * m; if (k === 'ArrowRight') d[v.h] = g * m; if (k === 'ArrowUp') d[v.v] = g * m; if (k === 'ArrowDown') d[v.v] = -g * m; if (k === 'PageUp') d[v.d] = g * m; if (k === 'PageDown') d[v.d] = -g * m;
      }
      app.nudge(d); e.preventDefault();
      return;
    }
    const id = Keys.match(e);
    if (id && runAction(id) !== false) e.preventDefault();
  }
  app.setGrid = function (g) {
    this.grid = g; $('#s-grid').value = String(g); this.invalidateAll(); this.status(_('ui.grid_set', { n: g }));
  };

  // ------------------------------------------------------------ Start
  const SAMPLES = [['sample-arena', 'sample.arena'], ['sample-breezy', 'sample.breezy']];
  app.init = function () {
    // Server, Anmeldung, Einstellungen und Karten auf dem Server (React-Dialoge)
    this.ui = initBackendUi({
      status: (m) => this.status(m),
      exportText: () => this.exportText(),
      isBsp: () => !!this.bsp,
      prechecks: () => Core.complexityItems(this.doc),
      loadText: (text, name, label) => this.loadText(text, name, label),
      openFile: (f) => this.openFile(f),
      addSound: (f, key) => this.addSound(f, key),
      soundNames: () => ({ wanted: Array.from(new Set(this.doc.ents.filter((e) => e.kv.classname === 'ambient_generic' && e.kv.message).map((e) => soundKey(e.kv.message)))), loaded: Array.from(this.sounds.keys()) }),
      openLocal: () => $('#f-map').click(),
      samples: () => SAMPLES.filter((x) => SAMPLE_TEXT[x[0]] !== undefined).map((x) => ({ id: x[0], label: _(x[1]) })),
      openSample: (id) => {
        const x = SAMPLES.find((y) => y[0] === id);
        try { this.loadText(SAMPLE_TEXT[id], _(x[1]), _('cmd.load_sample')); } catch (err) { this.status(_('ui.file_unreadable', { msg: err.message })); }
      },
      wadNames: () => this.wadNames,
      worldWad: () => this.doc.ents[0].kv.wad,
      worldFgd: () => this.doc.ents[0].kv[FGD_KEY] || '',
      fgdLoaded: () => Array.from(this.fgdTexts.keys()),
      addFgdText: (name, text) => this.addFgdText(name, text),
      getFileName: () => this.fileName,
      setFileName: (n) => {
        this.fileName = n; this.ui.chrome();
        // der Kartenname bestimmt die Vorgabe-FGD (op4_, de_, cs_), solange kein Mod-Profil gespeichert ist
        if (this.syncFgd()) { if (this.v3) this.v3.setDoc(this.doc); this.invalidateAll(); this.panels.docChanged(); }
      },
      setSettingsTab: (tab) => { this.settingsTab = tab; }
    });
    this.assets = createAssetHost(this.ui.session, this);   // Asset-Browser und Auswahlfelder (siehe ui/assets/)
    this.panels = Panels.create(this);
    // Werkzeuge
    const tools = $('#tools');
    TOOLS.forEach((t) => tools.append(h('button', { class: 'tool', 'data-tool': t.k, title: _(t.n) + ' (' + t.key + ')', 'aria-pressed': 'false', on: { click: () => app.setTool(t.k) } },
      (() => { const s = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); s.setAttribute('viewBox', '0 0 24 24'); s.innerHTML = t.svg; return s; })(), h('span', { class: 'tname', text: _(t.n) }), h('kbd', { text: t.key }))));
    // Raster
    const sg = $('#s-grid');
    GRIDS.forEach((g) => sg.append(h('option', { value: String(g), text: String(g), selected: g === app.grid })));
    sg.addEventListener('change', () => app.setGrid(parseInt(sg.value, 10)));
    $('#c-snap').addEventListener('change', (e) => { app.snapOn = e.target.checked; app.status(app.snapOn ? _('ui.snap_on') : _('ui.snap_off')); });
    document.addEventListener('pointerdown', (e) => { const m = $('#more-view'); if (m && m.open && !m.contains(e.target)) m.open = false; });
    $('#c-lock').addEventListener('change', (e) => { app.lock = e.target.checked; });
    // Ansichten
    app.v2.top = new View2D(app, $('#cv-top'), 'top');
    app.v2.front = new View2D(app, $('#cv-front'), 'front');
    app.v2.side = new View2D(app, $('#cv-side'), 'side');
    app.v3 = new View3D(app, $('#host3d'));
    app.views = [app.v3, app.v2.top, app.v2.front, app.v2.side];
    app.activeView = app.v2.top;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => app.resizeAll()) : null;
    $$('.pane').forEach((p) => { if (ro) ro.observe(p); });
    window.addEventListener('resize', () => { app.setLayout(app.layout, app.single); });
    $$('#vtabs [data-v]').forEach((b) => b.addEventListener('click', () => app.setLayout('single', b.dataset.v)));
    $$('[data-max]').forEach((b) => b.addEventListener('click', () => {
      if (app.layout === 'single') app.setLayout('quad'); else app.setLayout('single', b.dataset.max);
    }));
    $('#b-quad').addEventListener('click', () => app.setLayout('quad'));
    $('#b-fit').addEventListener('click', () => app.fit(false));
    $('#b-settings').addEventListener('click', () => app.openSettings());
    $('#b-save').addEventListener('click', () => app.openSave());
    $('#b-compile').addEventListener('click', () => app.ui.openCompile());
    const settingsChanged = () => app.ui.session.settingsChanged();
    I18n.onChange(settingsChanged); Theme.onChange(settingsChanged); Keys.onChange(settingsChanged);
    // letzte Mausposition je Ansicht (für das Kontextmenü per Tastatur)
    app.views.forEach((v) => { if (v.canvas) v.canvas.addEventListener('pointermove', (e) => { app.ptr = { view: v, x: e.clientX, y: e.clientY }; }); });
    Keys.onChange(() => app.refreshHints());
    app.refreshHints();
    $('#b-find').addEventListener('click', () => app.openFind());
    $('#b-check').addEventListener('click', () => app.openCheck());
    app.scheduleCheckFlag();
    $('#b-fly').addEventListener('click', () => app.toggleFly());
    $('#c-models').addEventListener('change', (e) => { app.showModels = e.target.checked; if (app.v3) { app.v3.dirty.geo = true; app.v3.invalidate(); } });
    $('#c-ents').addEventListener('change', (e) => { app.showEnts = e.target.checked; if (app.v3) { app.v3.dirty.geo = true; app.v3.setSel(); } app.invalidateAll(); });
    $('#c-aids').addEventListener('change', (e) => { app.showAids = e.target.checked; if (app.v3) app.v3.setSel(); app.invalidateAll(); });
    $('#b-light').addEventListener('click', () => app.toggleLight());
    $('#b-lrebake').addEventListener('click', () => app.bakeLight());
    const optChanged = () => { if (app.light.result) { app.light.stale = true; app.updateLightUi(); app.status(_('ui.light_opt_changed')); } };
    $('#s-lres').addEventListener('change', (e) => { app.light.res = parseInt(e.target.value, 10); optChanged(); });
    $('#c-shadow').addEventListener('change', (e) => { app.light.shadows = e.target.checked; optChanged(); });
    $('#c-bounce').addEventListener('change', (e) => { app.light.bounce = e.target.checked; optChanged(); });
    $('#c-flip').addEventListener('change', (e) => { app.light.flip = e.target.checked; optChanged(); if (app.v3) app.v3.setSel(); });
    $('#c-lmonly').addEventListener('change', (e) => { if (app.v3) app.v3.setView({ lmOnly: e.target.checked }); });
    $('#c-player').addEventListener('change', (e) => { if (app.v3) app.v3.setView({ player: e.target.checked }); });
    $('#b-lpop').addEventListener('click', () => { const p = $('#lpop'); p.hidden = !p.hidden; $('#b-lpop').setAttribute('aria-expanded', String(!p.hidden)); });
    $('#b-snap').addEventListener('click', () => app.snapshot());
    $('#c-hidesky').addEventListener('change', (e) => { if (app.v3) app.v3.setHideSky(e.target.checked); });
    $('#c-hideceil').addEventListener('change', (e) => { if (app.v3) app.v3.setHideCeilings(e.target.checked); });
    $('#c-skyon').addEventListener('change', (e) => app.setSkyOn(e.target.checked));
    $('#c-bgamma').addEventListener('change', (e) => { if (app.v3) app.v3.setBspGamma(e.target.checked); });
    $('#r-exp').addEventListener('input', (e) => { app.light.exposure = parseFloat(e.target.value); if (app.v3) app.v3.setExposure(app.light.exposure); });
    // Datei
    $('#b-new').addEventListener('click', () => app.newMap());
    $('#b-open').addEventListener('click', () => app.openMap());
    $('#f-map').addEventListener('change', async (e) => { await app.openFiles(Array.from(e.target.files)); e.target.value = ''; });
    $('#f-sky').addEventListener('change', async (e) => { await app.addSkyFiles(Array.from(e.target.files)); e.target.value = ''; });
    $('#f-snd').addEventListener('change', async (e) => { for (const f of Array.from(e.target.files)) await app.addSound(f); e.target.value = ''; });
    $('#f-spr').addEventListener('change', async (e) => { for (const f of Array.from(e.target.files)) await app.addSprite(f); e.target.value = ''; });
    $('#f-wad').addEventListener('change', async (e) => { for (const f of Array.from(e.target.files)) await app.addWad(f); e.target.value = ''; });
    $('#b-export').addEventListener('click', () => app.openExport());
    $('#b-undo').addEventListener('click', () => app.undo());
    $('#b-redo').addEventListener('click', () => app.redo());
    // Drag & Drop
    let dc = 0;
    window.addEventListener('dragenter', (e) => { e.preventDefault(); dc++; $('#drop').hidden = false; });
    window.addEventListener('dragleave', () => { dc = Math.max(0, dc - 1); if (!dc) $('#drop').hidden = true; });
    window.addEventListener('dragover', (e) => e.preventDefault());
    window.addEventListener('drop', async (e) => { e.preventDefault(); dc = 0; $('#drop').hidden = true; await app.openFiles(await collectDropped(e.dataTransfer || {})); });
    // Tastatur
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', (e) => { if (e.key === ' ') app.spaceDown = false; });
    window.addEventListener('blur', () => { app.spaceDown = false; });
    const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: light)');
    if (mq && mq.addEventListener) mq.addEventListener('change', () => app.themeChanged());
    new MutationObserver(() => app.themeChanged()).observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'data-skin', 'style', 'class'] });

    // Sprachwechsel: statische Texte neu setzen, dann alles neu zeichnen, was in JS gebaut wurde (kein Reload, damit nichts verloren geht)
    I18n.onChange(() => {
      app.ui.chrome();
      $$('#tools .tool').forEach((b) => { const tt = TOOLS.find((x) => x.k === b.dataset.tool); if (tt) b.querySelector('.tname').textContent = _(tt.n); });
      app.refreshHints(); app.renderOpts(); app.updateCounts(); app.updateLightUi(); app.panels.checkRes = null; app.panels.render();
      app.status(_('app.ready'));
    });
    I18n.applyDom();

    // Start: letzter Stand oder Beispiel
    let restored = false;
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (raw) {
        const s = JSON.parse(raw), r = Core.parseText(s.text);
        if (Core.allBrushes(r.doc).length || r.doc.ents.length > 1) {
          app.history = new Core.History(r.doc); app.fileName = s.name || 'neue_map';
          if (s.tex) app.activeTex = s.tex;
          restored = true; app.pendingVis = s.text;
        }
      }
    } catch (err) { restored = false; }
    if (!restored) {
      const sample = SAMPLE_TEXT['sample-arena'];
      if (sample) { const r = Core.parseText(sample); app.history = new Core.History(r.doc); app.fileName = 'breezy_bytes_arena'; }
    }
    app.setLayout(window.innerWidth <= 900 ? 'single' : 'quad', 'top');
    app.setTool('select');
    if (app.pendingVis) { const d = Groups.fromText(app.pendingVis); if (d) app.vis = Groups.restore(app.doc, d, Core.newId); app.pendingVis = null; app.visVer++; }
    app.syncFgd();   // Mod-Profil der Startkarte (auch die Vorgabe: Compiler-FGD, Spiel-FGD nach Kartenname)
    app.updateButtons(); app.updateCounts(); app.updateLightUi();
    if (app.v3.ok) app.v3.setDoc(app.doc);
    app.panels.docChanged(); app.panels.selChanged(); app.panels.wadsChanged();
    app.panels.showTab('props');
    app.resizeAll();
    app.fit(true);
    app.status(restored ? _('ui.restored') : _('ui.test_loaded'));
    app.ui.session.connect().catch(() => {});
    // Erst jetzt gelten Änderungen als ungesichert
    app.dirty = false;
  };

  return app;
})();
export default app;
